import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getUrl, remove, list } from 'aws-amplify/storage';
import Breadcrumbs from './Breadcrumbs';
import InlineRenameInput from './InlineRenameInput';
import BulkActionToolbar from './BulkActionToolbar';
import ConflictResolutionDialog from './ConflictResolutionDialog';
import SearchBar from './SearchBar';
import GridView from './GridView';
import ContextMenu from './ContextMenu';
import SelectionToggle from './SelectionToggle';
import { DownloadIcon, EditIcon, MoveIcon, DeleteIcon, FolderIcon, NewFolderIcon, UploadIcon, FileIcon } from './Icons';
import { renameFile, moveFile, upload as uploadFile, deleteFile, download as downloadFile } from '../services/FileService';
import { renameFolder, deleteFolder, listContents, moveFolder, createFolder as createFolderApi } from '../services/FolderService';
import { listAllFolders as listAllFoldersApi, searchItems as searchItemsApi } from '../services/fileMetadataApi';
import {
  createSelectionState,
  handleClick,
  handleCtrlClick,
  selectAll,
  clearSelection,
  isSelected,
  getSelectionCount,
  getSelectedItems
} from '../utils/SelectionManager';
import {
  createDragState,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  isItemBeingDragged,
  isDropTarget,
  canDropOnTarget,
  // External drag functions for file upload
  createExternalDragState,
  isExternalFileDrag,
  handleExternalDragEnter,
  handleExternalDragOver,
  handleExternalDragLeave,
  handleExternalDrop,
  resetExternalDragState,
  shouldShowDropZone,
  // Folder drop functions for recursive upload (Requirements 6.4, 6.5)
  containsFolder,
  processDroppedItems,
  createFolderUploadProgress,
  calculateFolderUploadProgress
} from '../utils/DragDropHandler';
import {
  loadPreferences,
  savePreferences
} from '../utils/PreferencesStorage';
import './FileExplorer.css';

const FileExplorer = () => {
  // The 'public' folder (ID: bb45c33f7cb26804) is the invisible root for users
  // Users start inside 'public' but never see it in the UI
  const PUBLIC_FOLDER_ID = 'bb45c33f7cb26804';
  
  const [currentFolder, setCurrentFolder] = useState(''); // Empty = root from user's perspective
  const [currentFolderId, setCurrentFolderId] = useState(PUBLIC_FOLDER_ID); // Start inside public folder
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderObjects, setFolderObjects] = useState([]); // Full folder objects with IDs
  const [folderIdMap, setFolderIdMap] = useState({}); // Map folder name to ID for current directory
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState('');
  const [fileToMove, setFileToMove] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [allFolders, setAllFolders] = useState([]);
  const [renamingFileKey, setRenamingFileKey] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renamingFolderName, setRenamingFolderName] = useState(null);
  const [isFolderRenaming, setIsFolderRenaming] = useState(false);
  const [folderRenameProgress, setFolderRenameProgress] = useState({ completed: 0, total: 0, currentItem: null });
  const [deletingFolderName, setDeletingFolderName] = useState(null);
  const [isFolderDeleting, setIsFolderDeleting] = useState(false);
  const [folderDeleteProgress, setFolderDeleteProgress] = useState({ completed: 0, total: 0, currentItem: null });
  
  // Bulk delete state for multi-select delete operations
  // Requirements: 8.5, 8.7
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ completed: 0, total: 0, currentItem: null });
  
  // Bulk move state for multi-select move operations
  // Requirements: 8.6, 8.7
  const [isBulkMoving, setIsBulkMoving] = useState(false);
  const [bulkMoveProgress, setBulkMoveProgress] = useState({ completed: 0, total: 0, currentItem: null });
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  
  // Batch upload state for multiple file upload via drag and drop
  // Requirement 6.3: Queue and upload multiple files with progress indication
  // Requirements 9.1, 9.2, 9.3 (s3-multipart-upload spec): Phase tracking for zip+upload flow
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchUploadProgress, setBatchUploadProgress] = useState({ 
    completed: 0, 
    total: 0, 
    currentFile: null,
    currentFileProgress: 0,
    errors: [],
    phase: 'uploading' // 'zipping' or 'uploading' - for combined zip+upload progress display
  });
  
  // Folder upload state for recursive folder upload via drag and drop
  // Requirements 6.4, 6.5: Support folder drop with recursive upload and retry
  const [folderUploadProgress, setFolderUploadProgress] = useState(createFolderUploadProgress());
  
  // Selection state for multi-select functionality
  // Requirements: 8.1, 8.2, 8.3
  const [selectedItems, setSelectedItems] = useState(createSelectionState());
  const [lastSelectedKey, setLastSelectedKey] = useState(null);
  
  // Selection mode state for toggle-based selection
  // Requirements: 3.2, 3.3, 3.4 (selection-mode-toggle spec)
  // - 3.2: WHEN a user clicks the Selection_Toggle button while in Normal_Mode, THE FileExplorer SHALL enter Selection_Mode
  // - 3.3: WHEN a user clicks the Selection_Toggle button while in Selection_Mode, THE FileExplorer SHALL exit Selection_Mode
  // - 3.4: WHEN exiting Selection_Mode, THE FileExplorer SHALL clear all current selections
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  
  // Drag and drop state for file/folder drag operations
  // Requirements: 5.1, 5.2, 5.6
  const [dragState, setDragState] = useState(createDragState());
  
  // External drag state for file upload via drag and drop from OS
  // Requirements: 6.1, 6.2
  // - 6.1: Display a visual drop zone indicator when external files are dragged over
  // - 6.2: Upload external files to the current folder when dropped
  const [externalDragState, setExternalDragState] = useState(createExternalDragState());
  const fileExplorerRef = useRef(null);
  const isLoadingRef = useRef(false); // Prevent duplicate listFilesAndFolders calls
  const currentFolderRef = useRef(''); // Track current folder path to avoid stale closure issues
  const dragTimeoutRef = useRef(null); // Safety timeout for resetting stuck drag state
  
  // Safety timeout effect: Reset drag state if it's been active for too long
  // This prevents the drop zone from getting "stuck" due to missed drag events
  useEffect(() => {
    if (externalDragState.showDropZone) {
      // Clear any existing timeout
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      // Set a safety timeout to reset the drag state after 10 seconds of inactivity
      dragTimeoutRef.current = setTimeout(() => {
        console.log('Safety timeout: Resetting stuck drag state');
        setExternalDragState(resetExternalDragState());
      }, 10000);
    } else {
      // Clear timeout when drag state is reset
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
    }
    
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, [externalDragState.showDropZone]);
  
  // Global drag leave listener: Reset drag state when drag leaves the document entirely
  // This catches cases where the drag leaves the window without triggering component events
  useEffect(() => {
    const handleDocumentDragLeave = (event) => {
      // Check if the drag is leaving the document entirely
      // relatedTarget will be null when leaving the window
      if (event.relatedTarget === null && externalDragState.showDropZone) {
        // Small delay to avoid race conditions with other drag events
        setTimeout(() => {
          setExternalDragState(prev => {
            // Only reset if still showing (another event might have already reset it)
            if (prev.showDropZone) {
              console.log('Document drag leave: Resetting drag state');
              return resetExternalDragState();
            }
            return prev;
          });
        }, 100);
      }
    };
    
    const handleDocumentDrop = (event) => {
      // Prevent default browser behavior for drops outside our drop zone
      // This prevents the browser from navigating to the dropped file
      if (externalDragState.showDropZone) {
        event.preventDefault();
      }
    };
    
    document.addEventListener('dragleave', handleDocumentDragLeave);
    document.addEventListener('drop', handleDocumentDrop);
    
    return () => {
      document.removeEventListener('dragleave', handleDocumentDragLeave);
      document.removeEventListener('drop', handleDocumentDrop);
    };
  }, [externalDragState.showDropZone]);
  
  // Conflict resolution dialog state for handling name conflicts during move operations
  // Requirements: 5.4, 5.5
  const [conflictDialog, setConflictDialog] = useState({
    isOpen: false,
    itemName: '',
    itemType: 'file',
    destinationFolder: '',
    resolve: null // Promise resolver function
  });
  
  // Pending move operation state for conflict resolution
  const [pendingMoveOperation, setPendingMoveOperation] = useState(null);
  
  // Search state for local folder filtering
  // Requirements: 7.2, 7.6
  // - 7.2: Filter the current view to show matching files and folders
  // - 7.6: Perform search filtering with minimal delay as the user types
  const [searchQuery, setSearchQuery] = useState('');
  
  // Global search state for searching across all folders
  // Requirements: 7.3, 7.4, 7.5
  // - 7.3: Global search toggle to search across all folders
  // - 7.4: Search results show folder path for each item
  // - 7.5: Clicking search result navigates to that location
  const [isGlobalSearch, setIsGlobalSearch] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearchLoading, setIsGlobalSearchLoading] = useState(false);
  
  // Sort state for file/folder sorting
  // Requirements: 9.1, 9.2, 9.4, 9.5
  // - 9.1: Provide sort options for name, date modified, and file size
  // - 9.2: Allow toggling between ascending and descending order
  // - 9.4: Persist the user's sort preference across sessions
  // - 9.5: Display visual indicator of current sort field and order
  const [sortBy, setSortBy] = useState(() => loadPreferences().sortBy);
  const [sortOrder, setSortOrder] = useState(() => loadPreferences().sortOrder);
  
  // View mode state for list/grid toggle
  // Requirements: 10.1, 10.3, 10.4, 10.5
  // - 10.1: Support a list view showing files in rows with details
  // - 10.3: Immediately switch the display format when user toggles view mode
  // - 10.4: Persist the user's view mode preference across sessions
  // - 10.5: Maintain the current selection when switching between views
  const [viewMode, setViewMode] = useState(() => loadPreferences().viewMode);

  // Context menu state for right-click actions on files
  // Requirement 11.1: WHEN a user right-clicks a file, THE Context_Menu SHALL display options
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    targetItem: null,
    targetType: null // 'file', 'folder', or 'empty'
  });

  /**
   * Sort comparison function for files.
   * Compares two files based on the current sort field and order.
   * 
   * Requirements: 9.1, 9.2
   * - 9.1: Provide sort options for name, date modified, and file size
   * - 9.2: Allow toggling between ascending and descending order
   * 
   * @param {Object} a - First file object
   * @param {Object} b - Second file object
   * @returns {number} Comparison result (-1, 0, or 1)
   */
  const compareFiles = useCallback((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'date':
        const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        comparison = dateA - dateB;
        break;
      case 'size':
        comparison = (a.size || 0) - (b.size || 0);
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  }, [sortBy, sortOrder]);

  /**
   * Sort comparison function for folders.
   * Folders are always sorted by name.
   * 
   * @param {string} a - First folder name
   * @param {string} b - Second folder name
   * @returns {number} Comparison result (-1, 0, or 1)
   */
  const compareFolders = useCallback((a, b) => {
    const comparison = a.localeCompare(b);
    return sortOrder === 'asc' ? comparison : -comparison;
  }, [sortOrder]);

  /**
   * Filter and sort files based on search query and sort settings.
   * Returns all files if search query is empty, sorted by current sort settings.
   * 
   * Requirements: 7.2, 7.6, 9.1, 9.2
   * - 7.2: Filter the current view to show matching files and folders
   * - 7.6: Perform search filtering with minimal delay as the user types
   * - 9.1: Provide sort options for name, date modified, and file size
   * - 9.2: Allow toggling between ascending and descending order
   */
  const filteredFiles = useMemo(() => {
    let result = files;
    
    // Apply search filter
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(file => 
        file.name.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    return [...result].sort(compareFiles);
  }, [files, searchQuery, compareFiles]);

  /**
   * Filter and sort folders based on search query and sort settings.
   * Returns all folders if search query is empty, sorted by current sort settings.
   * 
   * Requirements: 7.2, 7.6, 9.1, 9.2
   * - 7.2: Filter the current view to show matching files and folders
   * - 7.6: Perform search filtering with minimal delay as the user types
   */
  const filteredFolders = useMemo(() => {
    let result = folders;
    
    // Apply search filter
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(folder => 
        folder.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting (folders sorted by name)
    return [...result].sort(compareFolders);
  }, [folders, searchQuery, compareFolders]);

  /**
   * Filter and sort root folder structure based on search query and sort settings.
   * Uses folders from S3 (via listFilesAndFolders) instead of hardcoded list.
   * Returns all folders if search query is empty, sorted by current sort settings.
   * 
   * Requirements: 7.2, 7.6
   */
  const filteredFolderStructure = useMemo(() => {
    let result = [...folders];
    
    // Apply search filter
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(folder => 
        folder.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting (folders sorted by name)
    return result.sort(compareFolders);
  }, [searchQuery, compareFolders, folders]);

  /**
   * Handle search query change.
   * Updates the search state which triggers filtering via useMemo.
   * 
   * Requirement 7.6: Perform search filtering with minimal delay as the user types
   * (debouncing is handled by the SearchBar component)
   * 
   * @param {string} query - The new search query
   */
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  /**
   * Handle search clear.
   * Clears the search query and returns to normal folder view.
   * 
   * Requirement 7.5: When a user clears the search, return to the normal folder view
   */
  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    setGlobalSearchResults([]);
  }, []);

  /**
   * Toggle selection mode on/off.
   * Clears selection when exiting selection mode.
   * 
   * Requirements: 3.2, 3.3, 3.4 (selection-mode-toggle spec)
   * - 3.2: WHEN a user clicks the Selection_Toggle button while in Normal_Mode, THE FileExplorer SHALL enter Selection_Mode
   * - 3.3: WHEN a user clicks the Selection_Toggle button while in Selection_Mode, THE FileExplorer SHALL exit Selection_Mode
   * - 3.4: WHEN exiting Selection_Mode, THE FileExplorer SHALL clear all current selections
   */
  const handleSelectionModeToggle = useCallback(() => {
    setIsSelectionMode(prev => {
      if (prev) {
        // Exiting selection mode - clear selections
        setSelectedItems(createSelectionState());
        setLastSelectedKey(null);
      }
      return !prev;
    });
  }, []);

  /**
   * Handle global search toggle.
   * Switches between local folder filtering and global search across all folders.
   * 
   * Requirement 7.3: Global search toggle to search across all folders
   * 
   * @param {boolean} enabled - Whether global search is enabled
   */
  const handleGlobalSearchToggle = useCallback((enabled) => {
    setIsGlobalSearch(enabled);
    // Clear results when toggling
    if (!enabled) {
      setGlobalSearchResults([]);
    }
  }, []);

  /**
   * Handle sort field change.
   * Updates the sort field which triggers re-sorting via useMemo.
   * Saves the preference to localStorage for persistence across sessions.
   * 
   * Requirements: 9.1, 9.4
   * - 9.1: Provide sort options for name, date modified, and file size
   * - 9.4: Persist the user's sort preference across sessions
   * 
   * @param {string} field - The new sort field ('name', 'date', or 'size')
   */
  const handleSortByChange = useCallback((field) => {
    setSortBy(field);
    // Save preference to localStorage
    savePreferences({ sortBy: field, sortOrder, viewMode });
  }, [sortOrder, viewMode]);

  /**
   * Handle sort order toggle.
   * Toggles between ascending and descending order.
   * Saves the preference to localStorage for persistence across sessions.
   * 
   * Requirements: 9.2, 9.4
   * - 9.2: Allow toggling between ascending and descending order
   * - 9.4: Persist the user's sort preference across sessions
   */
  const handleSortOrderToggle = useCallback(() => {
    setSortOrder(prev => {
      const newOrder = prev === 'asc' ? 'desc' : 'asc';
      // Save preference to localStorage
      savePreferences({ sortBy, sortOrder: newOrder, viewMode });
      return newOrder;
    });
  }, [sortBy, viewMode]);

  /**
   * Handle view mode change.
   * Switches between list and grid view modes.
   * Selection is preserved automatically since selectedItems state is independent of view mode.
   * Saves the preference to localStorage for persistence across sessions.
   * 
   * Requirements: 10.1, 10.3, 10.4, 10.5
   * - 10.1: Support a list view showing files in rows with details
   * - 10.3: Immediately switch the display format when user toggles view mode
   * - 10.4: Persist the user's view mode preference across sessions
   * - 10.5: Maintain the current selection when switching between views
   * 
   * @param {string} mode - The new view mode ('list' or 'grid')
   */
  const handleViewModeChange = useCallback((mode) => {
    // Cancel any active rename when switching views
    setRenamingFileKey(null);
    setRenamingFolderName(null);
    setViewMode(mode);
    // Save preference to localStorage
    savePreferences({ sortBy, sortOrder, viewMode: mode });
  }, [sortBy, sortOrder]);

  /**
   * Handle right-click context menu on file items.
   * Shows the context menu at the mouse position with file-specific options.
   * 
   * Requirement 11.1: WHEN a user right-clicks a file, THE Context_Menu SHALL display
   * options: Download, Rename, Move, Delete
   * 
   * @param {MouseEvent} event - The context menu event
   * @param {Object} file - The file object that was right-clicked
   */
  const handleFileContextMenu = useCallback((event, file) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      targetItem: file,
      targetType: 'file'
    });
  }, []);

  /**
   * Close the context menu.
   * Called when user clicks elsewhere, presses Escape, or selects an action.
   * 
   * Requirement 11.5: THE Context_Menu SHALL close when the user clicks elsewhere or presses Escape
   */
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      targetItem: null,
      targetType: null
    });
  }, []);

  /**
   * Perform global search across all folders.
   * Lists all items recursively from root and filters by search query.
   * 
   * Requirements: 7.3, 7.4
   * - 7.3: Global search toggle to search across all folders
   * - 7.4: Search results show folder path for each item
   * 
   * @param {string} query - The search query
   */
  const performGlobalSearch = useCallback(async (query) => {
    if (!query || query.trim() === '') {
      setGlobalSearchResults([]);
      return;
    }

    setIsGlobalSearchLoading(true);
    
    try {
      // Use the search API endpoint (single API call with server-side filtering)
      const response = await searchItemsApi(query, { 
        rootFolderId: PUBLIC_FOLDER_ID,
        limit: 100 
      });
      
      // Transform results to match expected format
      const results = response.items.map(item => {
        const folderPath = item.folderPath;
        const fullPath = folderPath === '(root)' 
          ? item.name 
          : `${folderPath}/${item.name}`;
        
        return {
          key: fullPath,
          id: item.id,
          name: item.name,
          parentFolderId: item.parentFolderId, // Include for direct navigation
          folderPath: folderPath,
          size: item.size,
          lastModified: item.updatedAt || item.createdAt,
          type: item.type
        };
      });
      
      setGlobalSearchResults(results);
    } catch (error) {
      console.error('Error performing global search:', error);
      setError('Failed to search files. Please try again.');
      setGlobalSearchResults([]);
    } finally {
      setIsGlobalSearchLoading(false);
    }
  }, []);

  /**
   * Effect to trigger global search when query changes.
   * Always searches across all folders.
   */
  useEffect(() => {
    if (searchQuery) {
      performGlobalSearch(searchQuery);
    } else {
      setGlobalSearchResults([]);
    }
  }, [searchQuery, performGlobalSearch]);

  /**
   * Navigate to a search result location.
   * Sets the current folder to the result's folder path.
   * 
   * Requirement 7.5: Clicking search result navigates to that location
   * 
   * @param {Object} result - The search result to navigate to
   */
  const navigateToSearchResult = useCallback(async (result) => {
    // Clear search state
    setSearchQuery('');
    setIsGlobalSearch(false);
    setGlobalSearchResults([]);
    
    let targetPath;
    let folderId;
    
    if (result.type === 'folder') {
      // Navigate into the folder itself
      targetPath = result.key;
      folderId = result.id; // Use the folder's own ID
    } else {
      // Navigate to the folder containing the file
      targetPath = result.folderPath === '(root)' ? '' : result.folderPath;
      // Use parentFolderId if available (from new search API), otherwise resolve from path
      folderId = result.parentFolderId || (targetPath ? await resolveFolderIdFromPath(targetPath) : PUBLIC_FOLDER_ID);
    }
    
    // Update ref immediately
    currentFolderRef.current = targetPath;
    
    // Update state
    setCurrentFolder(targetPath);
    setCurrentFolderId(folderId || PUBLIC_FOLDER_ID);
    
    // Clear selection when navigating
    setSelectedItems(createSelectionState());
    setLastSelectedKey(null);
    
    // Load the folder contents
    if (folderId) {
      isLoadingRef.current = false;
      listFilesAndFolders(folderId, targetPath);
    }
  }, []);

  /**
   * Check if there are no search results.
   * Returns true if search is active but no items match.
   */
  const hasNoSearchResults = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return false;
    }
    
    // For global search, check global results
    if (isGlobalSearch) {
      return !isGlobalSearchLoading && globalSearchResults.length === 0;
    }
    
    // Check based on whether we're at root or in a subfolder
    if (!currentFolder) {
      return filteredFolderStructure.length === 0;
    }
    return filteredFiles.length === 0 && filteredFolders.length === 0;
  }, [searchQuery, currentFolder, filteredFiles, filteredFolders, filteredFolderStructure, isGlobalSearch, isGlobalSearchLoading, globalSearchResults]);

  useEffect(() => {
    // Reset loading ref when folder ID changes to allow new load
    isLoadingRef.current = false;
    // Trigger on currentFolderId changes (DynamoDB mode) or currentFolder changes (S3 fallback mode)
    // currentFolderId is the source of truth for DynamoDB mode
    // currentFolder is used for S3 fallback mode when currentFolderId is null
    listFilesAndFolders(currentFolderId, currentFolder);
  }, [currentFolderId, currentFolder]); // Depend on both for S3 fallback support

  /**
   * Handle Escape key to exit Selection Mode.
   * Only handles Escape when in Selection Mode and no modals are open.
   * 
   * Requirement 8.2 (selection-mode-toggle spec):
   * WHEN a user presses the Escape key while in Selection_Mode, 
   * THE FileExplorer SHALL exit Selection_Mode and clear all selections
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle Escape key
      if (event.key !== 'Escape') return;
      
      // Only handle when in Selection Mode
      if (!isSelectionMode) return;
      
      // Don't handle if a modal is open (check for common modal states)
      if (showMoveModal || showBulkMoveModal || conflictDialog.isOpen || showNewFolderInput) {
        return;
      }
      
      // Exit Selection Mode and clear selections
      setIsSelectionMode(false);
      setSelectedItems(createSelectionState());
      setLastSelectedKey(null);
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSelectionMode, showMoveModal, showBulkMoveModal, conflictDialog.isOpen, showNewFolderInput]);

  // Resolve folder ID from path (for initial load and breadcrumb navigation)
  // Resolve folder ID from path, starting from the public folder (user's root)
  const resolveFolderIdFromPath = async (path) => {
    if (!path) return PUBLIC_FOLDER_ID; // Empty path = public folder (user's root)
    
    const parts = path.split('/').filter(p => p);
    let currentId = PUBLIC_FOLDER_ID; // Start from public folder, not ROOT
    
    for (const part of parts) {
      const { folders: subFolders } = await listContents(currentId);
      const folder = subFolders.find(f => f.name === part);
      if (folder) {
        currentId = folder.id;
      } else {
        console.warn(`Folder "${part}" not found in path "${path}"`);
        return null;
      }
    }
    
    return currentId;
  };

  const listFilesAndFolders = async (folderId = currentFolderId, folderPath = currentFolder) => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      console.log('listFilesAndFolders: Already loading, skipping');
      return;
    }
    isLoadingRef.current = true;
    
    try {
      // Use passed folderId, or default to PUBLIC_FOLDER_ID
      let targetFolderId = folderId || PUBLIC_FOLDER_ID;
      console.log('listFilesAndFolders start:', { folderPath, folderId, targetFolderId });
      
      // If we have a path but no ID, resolve it from the path
      if (!targetFolderId && folderPath) {
        targetFolderId = await resolveFolderIdFromPath(folderPath);
        console.log('Resolved folder ID from path:', { folderPath, targetFolderId });
      }
      
      // Default to public folder if no ID resolved
      if (!targetFolderId) {
        targetFolderId = PUBLIC_FOLDER_ID;
      }
      
      console.log('Listing contents of folder:', targetFolderId);
      // Use the new DynamoDB-based API to list folder contents
      const { files: apiFiles, folders: apiFolders } = await listContents(targetFolderId);
      console.log('API response:', { filesCount: apiFiles.length, foldersCount: apiFolders.length, folders: apiFolders });
      
      // Store full folder objects for ID lookups
      setFolderObjects(apiFolders);
      
      // Build folder ID map for rename/delete operations
      const idMap = {};
      apiFolders.forEach(folder => {
        idMap[folder.name] = folder.id;
      });
      setFolderIdMap(idMap);
      
      // Transform files to match expected format (with key property for compatibility)
      const transformedFiles = apiFiles.map(file => ({
        ...file,
        key: folderPath ? `${folderPath}/${file.name}` : file.name,
        lastModified: file.updatedAt || file.createdAt
      }));
      
      // Extract folder names for display (keeping backward compatibility)
      const folderNames = apiFolders.map(folder => folder.name);
      
      setFiles(transformedFiles);
      setFolders(folderNames);
    } catch (error) {
      console.log('Error listing files and folders via API: ', error);
      // Fallback to S3 listing if DynamoDB API fails
      try {
        const fileList = await list({
          prefix: folderPath ? `${folderPath}/` : ''
        });
        
        // Process the results to separate files and folders
        const processedItems = processS3Items(fileList.items, folderPath);
        setFiles(processedItems.files);
        setFolders(processedItems.folders);
        setFolderObjects([]); // Clear folder objects when using S3 fallback
        setFolderIdMap({}); // Clear ID map when using S3 fallback
      } catch (s3Error) {
        console.log('S3 fallback also failed:', s3Error);
        setError('Failed to load files and folders. Please try again.');
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  // Process S3 items to separate files and folders
  const processS3Items = (items, currentPath) => {
    const files = [];
    const folders = new Set();
    const currentPrefix = currentPath ? `${currentPath}/` : '';
    
    items.forEach(item => {
      // Skip the current folder itself
      if (item.key === currentPrefix) return;
      
      // Remove the current path prefix to get the relative path
      const relativePath = item.key.slice(currentPrefix.length);
      
      // Check if this is a folder (contains '/' in the relative path)
      if (relativePath.includes('/')) {
        // Extract the folder name (first segment of the relative path)
        const folderName = relativePath.split('/')[0];
        folders.add(folderName);
      } else {
        // This is a file in the current directory
        files.push({
          ...item,
          name: relativePath
        });
      }
    });
    
    return {
      files,
      folders: Array.from(folders)
    };
  };

  // Fetch all folders in a single API call (for folder picker)
  const fetchAllFolders = async () => {
    try {
      // Use the optimized API endpoint that returns all folders in one call
      const response = await listAllFoldersApi();
      
      // Add root option at the beginning
      const folderList = [
        { path: '', id: PUBLIC_FOLDER_ID, name: '(Root)' },
        ...response.folders.map(folder => ({
          path: folder.path,
          id: folder.id,
          name: folder.name
        }))
      ];
      
      return folderList;
    } catch (error) {
      console.error('Error fetching all folders:', error);
      return [{ path: '', id: PUBLIC_FOLDER_ID, name: '(Root)' }];
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Don't set upload status for file selection
      // setUploadStatus(`Selected: ${file.name} (${formatFileSize(file.size)})`);
    }
  };

  const startUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('No file selected');
      return;
    }
    
    // Calculate file size in MB
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    console.log(`File size: ${fileSizeMB.toFixed(2)} MB`);
    setUploadStatus(`Preparing to upload ${fileSizeMB.toFixed(2)} MB file...`);
    
    try {
      setUploading(true);
      setUploadProgress(0);
      
      // Use the new FileService upload which creates DynamoDB record
      await uploadFile(
        selectedFile,
        currentFolderId, // Pass folder ID, not path
        (progress) => {
          setUploadProgress(progress);
          const loadedMB = ((selectedFile.size * progress / 100) / (1024 * 1024)).toFixed(2);
          const totalMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
          setUploadStatus(`Uploading: ${progress}% (${loadedMB} MB of ${totalMB} MB)`);
        }
      );
      
      setUploadProgress(100);
      setUploadStatus(`Upload complete! (${fileSizeMB.toFixed(2)} MB)`);
      
      console.log('Upload completed successfully');
      setUploading(false);
      setSelectedFile(null);
      // Reset the file input
      document.getElementById('file-upload').value = '';
      setTimeout(() => setUploadStatus(''), 3000);
      listFilesAndFolders();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploading(false);
      setUploadStatus(`Upload failed: ${error.message}`);
    }
  };

  const handleDownload = async (fileIdOrKey) => {
    try {
      // Use the new FileService download which handles both new IDs and legacy keys
      await downloadFile(fileIdOrKey);
    } catch (error) {
      console.log('Error downloading file: ', error);
      setError('Failed to download file. Please try again.');
    }
  };
  const handleDelete = async (file) => {
    // Show confirmation dialog before deleting
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return; // User canceled the deletion
    }
    
    try {
      // Use file ID if available (DynamoDB), otherwise fall back to key (legacy S3)
      const identifier = file.id || file.key;
      await deleteFile(identifier);
      listFilesAndFolders();
    } catch (error) {
      console.log('Error deleting file: ', error);
      setError('Failed to delete file. Please try again.');
    }
  };

  /**
   * Handle context menu action for downloading a file.
   * Closes the menu and triggers the download.
   * 
   * Requirement 11.1: Download option in file context menu
   */
  const handleContextMenuDownload = useCallback(() => {
    if (contextMenu.targetItem && contextMenu.targetType === 'file') {
      handleDownload(contextMenu.targetItem.id);
    }
    handleCloseContextMenu();
  }, [contextMenu.targetItem, contextMenu.targetType, handleCloseContextMenu]);

  /**
   * Handle context menu action for renaming a file.
   * Closes the menu and triggers inline rename mode.
   * 
   * Requirement 11.1: Rename option in file context menu
   */
  const handleContextMenuRename = useCallback(() => {
    if (contextMenu.targetItem && contextMenu.targetType === 'file') {
      setRenamingFileKey(contextMenu.targetItem.key);
    }
    handleCloseContextMenu();
  }, [contextMenu.targetItem, contextMenu.targetType, handleCloseContextMenu]);

  /**
   * Handle context menu action for moving a file.
   * Closes the menu and opens the move modal.
   * 
   * Requirement 11.1: Move option in file context menu
   */
  const handleContextMenuMove = useCallback(async () => {
    if (contextMenu.targetItem && contextMenu.targetType === 'file') {
      setFileToMove(contextMenu.targetItem);
      const folders = await fetchAllFolders();
      setAllFolders(folders);
      setShowMoveModal(true);
    }
    handleCloseContextMenu();
  }, [contextMenu.targetItem, contextMenu.targetType, handleCloseContextMenu]);

  /**
   * Handle context menu action for deleting a file.
   * Closes the menu and triggers the delete confirmation.
   * 
   * Requirement 11.1: Delete option in file context menu
   */
  const handleContextMenuDelete = useCallback(() => {
    if (contextMenu.targetItem && contextMenu.targetType === 'file') {
      handleDelete(contextMenu.targetItem);
    }
    handleCloseContextMenu();
  }, [contextMenu.targetItem, contextMenu.targetType, handleCloseContextMenu]);

  /**
   * Get context menu items for file right-click.
   * Returns array of menu items with labels, icons, and click handlers.
   * 
   * Requirement 11.1: Menu options: Download, Rename, Move, Delete
   * 
   * @returns {Array} Array of menu item objects
   */
  const getFileContextMenuItems = useCallback(() => {
    const isDisabled = isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving;
    
    return [
      {
        label: 'Download',
        icon: <DownloadIcon size={16} />,
        onClick: handleContextMenuDownload,
        disabled: isDisabled
      },
      {
        label: 'Rename',
        icon: <EditIcon size={16} />,
        onClick: handleContextMenuRename,
        disabled: isDisabled || renamingFileKey !== null
      },
      {
        label: 'Move',
        icon: <MoveIcon size={16} />,
        onClick: handleContextMenuMove,
        disabled: isDisabled
      },
      { divider: true },
      {
        label: 'Delete',
        icon: <DeleteIcon size={16} />,
        onClick: handleContextMenuDelete,
        disabled: isDisabled
      }
    ];
  }, [
    isRenaming,
    isFolderRenaming,
    isFolderDeleting,
    isBulkDeleting,
    isBulkMoving,
    renamingFileKey,
    handleContextMenuDownload,
    handleContextMenuRename,
    handleContextMenuMove,
    handleContextMenuDelete
  ]);

  /**
   * Handle right-click context menu on folder items.
   * Shows the context menu at the mouse position with folder-specific options.
   * 
   * Requirement 11.2: WHEN a user right-clicks a folder, THE Context_Menu SHALL display
   * options: Open, Rename, Delete
   * 
   * @param {MouseEvent} event - The context menu event
   * @param {string} folderName - The name of the folder that was right-clicked
   */
  const handleFolderContextMenu = useCallback((event, folderName) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      targetItem: folderName,
      targetType: 'folder'
    });
  }, []);

  /**
   * Handle context menu action for opening a folder.
   * Closes the menu and navigates into the folder.
   * 
   * Requirement 11.2: Open option in folder context menu
   */
  const handleContextMenuFolderOpen = useCallback(() => {
    if (contextMenu.targetItem && contextMenu.targetType === 'folder') {
      const folderName = contextMenu.targetItem;
      const folderId = folderIdMap[folderName];
      // Use ref to get current path to avoid stale closure issues
      const currentPath = currentFolderRef.current;
      const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
      
      // Update ref immediately
      currentFolderRef.current = newPath;
      
      // Update state
      setCurrentFolder(newPath);
      setCurrentFolderId(folderId || null);
      
      // Clear selection when navigating
      setSelectedItems(createSelectionState());
      setLastSelectedKey(null);
      
      // Load the folder contents
      if (folderId) {
        isLoadingRef.current = false;
        listFilesAndFolders(folderId, newPath);
      }
    }
    handleCloseContextMenu();
  }, [contextMenu.targetItem, contextMenu.targetType, folderIdMap, handleCloseContextMenu]);

  /**
   * Handle context menu action for renaming a folder.
   * Closes the menu and triggers inline rename mode for the folder.
   * 
   * Requirement 11.2: Rename option in folder context menu
   */
  const handleContextMenuFolderRename = useCallback(() => {
    if (contextMenu.targetItem && contextMenu.targetType === 'folder') {
      setRenamingFolderName(contextMenu.targetItem);
    }
    handleCloseContextMenu();
  }, [contextMenu.targetItem, contextMenu.targetType, handleCloseContextMenu]);

  /**
   * Handle context menu action for deleting a folder.
   * Closes the menu and triggers the folder delete confirmation.
   * 
   * Requirement 11.2: Delete option in folder context menu
   */
  const handleContextMenuFolderDelete = useCallback(() => {
    if (contextMenu.targetItem && contextMenu.targetType === 'folder') {
      handleFolderDelete(contextMenu.targetItem);
    }
    handleCloseContextMenu();
  }, [contextMenu.targetItem, contextMenu.targetType, handleCloseContextMenu]);

  /**
   * Get context menu items for folder right-click.
   * Returns array of menu items with labels, icons, and click handlers.
   * 
   * Requirement 11.2: Menu options: Open, Rename, Delete
   * 
   * @returns {Array} Array of menu item objects
   */
  const getFolderContextMenuItems = useCallback(() => {
    const isDisabled = isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving;
    
    return [
      {
        label: 'Open',
        icon: <FolderIcon size={16} />,
        onClick: handleContextMenuFolderOpen,
        disabled: isDisabled
      },
      {
        label: 'Rename',
        icon: <EditIcon size={16} />,
        onClick: handleContextMenuFolderRename,
        disabled: isDisabled || renamingFolderName !== null
      },
      { divider: true },
      {
        label: 'Delete',
        icon: <DeleteIcon size={16} />,
        onClick: handleContextMenuFolderDelete,
        disabled: isDisabled
      }
    ];
  }, [
    isRenaming,
    isFolderRenaming,
    isFolderDeleting,
    isBulkDeleting,
    isBulkMoving,
    renamingFolderName,
    handleContextMenuFolderOpen,
    handleContextMenuFolderRename,
    handleContextMenuFolderDelete
  ]);

  /**
   * Handle right-click context menu on empty space in the file explorer.
   * Shows the context menu at the mouse position with empty space options.
   * 
   * Requirement 11.3: WHEN a user right-clicks in empty space, THE Context_Menu SHALL display
   * options: New Folder, Upload Files
   * 
   * @param {MouseEvent} event - The context menu event
   */
  const handleEmptySpaceContextMenu = useCallback((event) => {
    // Only handle right-click on the container itself, not on items
    // Check if the click target is the container or an empty area
    const target = event.target;
    const isEmptySpace = 
      target.classList.contains('file-list') ||
      target.classList.contains('folder-grid') ||
      target.classList.contains('folders-list') ||
      target.classList.contains('file-explorer-content') ||
      target.tagName === 'UL' ||
      target.tagName === 'H3' ||
      target.tagName === 'P';
    
    // Don't show empty space menu if clicking on a file or folder item
    if (!isEmptySpace) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      targetItem: null,
      targetType: 'empty'
    });
  }, []);

  /**
   * Handle context menu action for creating a new folder.
   * Closes the menu and shows the new folder input.
   * 
   * Requirement 11.3: New Folder option in empty space context menu
   */
  const handleContextMenuNewFolder = useCallback(() => {
    setShowNewFolderInput(true);
    setNewFolderName('');
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  /**
   * Handle context menu action for uploading files.
   * Closes the menu and triggers the file input click.
   * 
   * Requirement 11.3: Upload Files option in empty space context menu
   */
  const handleContextMenuUploadFiles = useCallback(() => {
    // Trigger the file input click to open file picker
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.click();
    }
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  /**
   * Get context menu items for empty space right-click.
   * Returns array of menu items with labels, icons, and click handlers.
   * 
   * Requirement 11.3: Menu options: New Folder, Upload Files
   * 
   * @returns {Array} Array of menu item objects
   */
  const getEmptySpaceContextMenuItems = useCallback(() => {
    const isDisabled = isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving || uploading;
    
    return [
      {
        label: 'New Folder',
        icon: <NewFolderIcon size={16} />,
        onClick: handleContextMenuNewFolder,
        disabled: isDisabled || showNewFolderInput
      },
      {
        label: 'Upload Files',
        icon: <UploadIcon size={16} />,
        onClick: handleContextMenuUploadFiles,
        disabled: isDisabled
      }
    ];
  }, [
    isRenaming,
    isFolderRenaming,
    isFolderDeleting,
    isBulkDeleting,
    isBulkMoving,
    uploading,
    showNewFolderInput,
    handleContextMenuNewFolder,
    handleContextMenuUploadFiles
  ]);

  const handleMoveFile = async (destinationFolder) => {
    if (!fileToMove) return;
    
    try {
      setIsMoving(true);
      
      // destinationFolder is now an object with { path, id, name }
      const destinationFolderId = destinationFolder?.id || PUBLIC_FOLDER_ID;
      
      // Check if file has an ID (DynamoDB-managed) or is legacy (S3-only)
      if (fileToMove.id) {
        // Use O(1) DynamoDB move
        await moveFile(fileToMove.id, destinationFolderId);
      } else {
        // Legacy file - use S3 copy+delete (slower)
        const fileName = fileToMove.name;
        const sourceKey = fileToMove.key;
        const destinationPath = destinationFolder?.path || '';
        const destinationKey = destinationPath 
          ? `${destinationPath}/${fileName}` 
          : fileName;
        
        // Skip if source and destination are the same
        if (sourceKey === destinationKey) {
          setShowMoveModal(false);
          setIsMoving(false);
          return;
        }
        
        // Use Amplify copy for legacy files
        const { copy } = await import('aws-amplify/storage');
        await copy({ source: { key: sourceKey }, destination: { key: destinationKey } });
        await remove({ key: sourceKey });
      }
      
      // Refresh the file list
      listFilesAndFolders();
      
      // Close the modal
      setShowMoveModal(false);
      setIsMoving(false);
    } catch (error) {
      console.error('Error moving file:', error);
      setError('Failed to move file. Please try again.');
      setIsMoving(false);
    }
  };

  /**
   * Handle file rename submission.
   * Uses FileService.renameFile for DynamoDB-managed files (O(1) operation).
   * Falls back to legacy S3 copy+delete for unmigrated files.
   * Shows loading state during the operation and refreshes the file list on success.
   * 
   * Requirements: 3.2, 3.5
   * - 3.2: Copy the file to the new key and delete the original
   * - 3.5: Preserve the file extension during rename unless explicitly changed
   * 
   * @param {string} fileKeyOrId - The file key or ID
   * @param {string} newName - The new name for the file
   */
  const handleFileRename = async (fileKeyOrId, newName) => {
    if (!fileKeyOrId || !newName) {
      return;
    }

    try {
      setIsRenaming(true);
      setError('');

      // Find the file object to check if it has an ID
      const file = files.find(f => f.key === fileKeyOrId || f.id === fileKeyOrId);
      
      if (file && file.id) {
        // Use O(1) DynamoDB rename
        await renameFile(file.id, newName);
      } else {
        // Legacy file - throw error since migration is required
        throw new Error('Legacy files must be migrated before renaming. Please run the migration script.');
      }

      // Clear the renaming state
      setRenamingFileKey(null);
      setIsRenaming(false);

      // Refresh the file list to show the renamed file
      listFilesAndFolders();
    } catch (error) {
      console.error('Error renaming file:', error);
      setError(`Failed to rename file: ${error.message}`);
      setIsRenaming(false);
      // Keep the rename input open so user can try again
    }
  };

  /**
   * Cancel the file rename operation.
   * Clears the renaming state without making any changes.
   */
  const handleCancelRename = () => {
    setRenamingFileKey(null);
    setError('');
  };

  /**
   * Handle folder rename submission.
   * Uses DynamoDB-based FolderService for O(1) rename operation.
   * Shows progress indicator during the operation and refreshes the view on success.
   * 
   * Requirements: 2.2, 2.5, 2.6
   * - 2.2: Update folder name in DynamoDB (O(1) operation)
   * - 2.5: Display a progress indicator during folder rename
   * - 2.6: Refresh the view on success
   * 
   * @param {string} folderName - The current name of the folder being renamed
   * @param {string} newName - The new name for the folder
   */
  const handleFolderRename = async (folderName, newName) => {
    if (!folderName || !newName) {
      return;
    }

    try {
      setIsFolderRenaming(true);
      setFolderRenameProgress({ completed: 0, total: 1, currentItem: folderName });
      setError('');

      // Skip if the name hasn't changed
      if (folderName === newName) {
        setRenamingFolderName(null);
        setIsFolderRenaming(false);
        return;
      }

      // Get the folder ID from the map
      const folderId = folderIdMap[folderName];
      if (!folderId) {
        throw new Error(`Folder ID not found for "${folderName}". This may be a legacy folder that needs migration.`);
      }

      // Progress callback to update the UI during rename
      const onProgress = (progress) => {
        setFolderRenameProgress({
          completed: progress.completed,
          total: progress.total,
          currentItem: progress.currentItem
        });
      };

      // Call FolderService to rename the folder (O(1) DynamoDB update)
      await renameFolder(folderId, newName, onProgress);

      // Clear the renaming state
      setRenamingFolderName(null);
      setIsFolderRenaming(false);
      setFolderRenameProgress({ completed: 0, total: 0, currentItem: null });

      // Refresh the file list to show the renamed folder
      listFilesAndFolders();
    } catch (error) {
      console.error('Error renaming folder:', error);
      setError(`Failed to rename folder: ${error.message}`);
      setIsFolderRenaming(false);
      setFolderRenameProgress({ completed: 0, total: 0, currentItem: null });
      // Keep the rename input open so user can try again
    }
  };

  /**
   * Cancel the folder rename operation.
   * Clears the renaming state without making any changes.
   */
  const handleCancelFolderRename = () => {
    setRenamingFolderName(null);
    setError('');
  };

  /**
   * Get all items in the current view (files and folders combined)
   * Used for selection operations that need the full list of items.
   * 
   * @returns {Array<Object>} Combined array of files and folders with consistent key property
   */
  const getAllItems = () => {
    // Convert folders to objects with key property for consistency
    const folderItems = folders.map(folder => ({
      key: folder,
      name: folder,
      type: 'folder'
    }));
    
    // Files already have key property
    const fileItems = files.map(file => ({
      ...file,
      type: 'file'
    }));
    
    // Return folders first, then files (standard file explorer ordering)
    return [...folderItems, ...fileItems];
  };

  /**
   * Handle item selection based on click event and modifier keys.
   * Supports single click, Ctrl/Cmd+click toggle, and Shift+click range selection.
   * 
   * Requirements: 8.1, 8.2
   * - 8.1: Allow selecting multiple files via Ctrl/Cmd+click
   * - 8.2: Support Shift+click to select a range of files
   * Handle file click based on current mode.
   * In Normal Mode: download/preview file (existing behavior), unless Ctrl/Cmd+click for selection
   * In Selection Mode: toggle selection
   * 
   * Requirements: 1.3, 4.2 (selection-mode-toggle spec)
   * - 1.3: WHEN a user single-clicks a file in Normal_Mode, THE FileExplorer SHALL initiate a download or preview of that file
   * - 4.2: WHEN a user single-clicks an item in Selection_Mode, THE FileExplorer SHALL toggle that item's selection state
   * 
   * Design note: In Normal Mode, Ctrl/Cmd+click triggers selection (power user feature)
   * 
   * @param {string} itemKey - The key of the clicked item
   * @param {Object} event - The click event with modifier keys
   */
  const handleItemSelect = (itemKey, event) => {
    // Prevent action when clicking action buttons
    if (event.target.tagName === 'BUTTON') {
      return;
    }
    
    // Check if we should trigger selection behavior
    // In Selection Mode: always select
    // In Normal Mode: only select if Ctrl/Cmd is held (power user feature)
    const shouldSelect = isSelectionMode || event.ctrlKey || event.metaKey;
    
    if (shouldSelect) {
      // Selection Mode or Ctrl/Cmd+click: toggle selection
      const allItems = getAllItems();
      const result = handleClick(itemKey, event, allItems, lastSelectedKey, selectedItems);
      setSelectedItems(result.selection);
      setLastSelectedKey(result.lastSelected);
    } else {
      // Normal Mode without modifiers: download/preview file
      // Find the file by key to get its id for the new download API
      const file = files.find(f => f.key === itemKey);
      if (file && file.id) {
        handleDownload(file.id);
      } else {
        // Fallback to key for legacy files
        handleDownload(itemKey);
      }
    }
  };

  /**
   * Handle folder click based on current mode.
   * In Normal Mode: navigate to folder (existing behavior), unless Ctrl/Cmd+click for selection
   * In Selection Mode: toggle selection
   * 
   * Requirements: 1.2, 4.2 (selection-mode-toggle spec)
   * - 1.2: WHEN a user single-clicks a folder in Normal_Mode, THE FileExplorer SHALL navigate into that folder
   * - 4.2: WHEN a user single-clicks an item in Selection_Mode, THE FileExplorer SHALL toggle that item's selection state
   * 
   * Design note: In Normal Mode, Ctrl/Cmd+click triggers selection (power user feature)
   * 
   * @param {string} folderName - The name of the clicked folder
   * @param {Object} event - The click event with modifier keys
   */
  const handleFolderClick = (folderName, event) => {
    // Prevent action when clicking action buttons
    if (event.target.tagName === 'BUTTON') {
      return;
    }
    
    // Check if we should trigger selection behavior
    // In Selection Mode: always select
    // In Normal Mode: only select if Ctrl/Cmd is held (power user feature)
    const shouldSelect = isSelectionMode || event.ctrlKey || event.metaKey;
    
    if (shouldSelect) {
      // Selection Mode or Ctrl/Cmd+click: toggle selection
      const allItems = getAllItems();
      const result = handleClick(folderName, event, allItems, lastSelectedKey, selectedItems);
      setSelectedItems(result.selection);
      setLastSelectedKey(result.lastSelected);
    } else {
      // Normal Mode without modifiers: navigate to folder
      navigateToFolder(folderName);
    }
  };

  /**
   * Handle Select All functionality.
   * Selects all files and folders in the current view.
   * 
   * Requirement 8.3: Provide a "Select All" option for the current folder
   */
  const handleSelectAll = () => {
    const allItems = getAllItems();
    const newSelection = selectAll(allItems);
    setSelectedItems(newSelection);
    
    // Set last selected to the last item in the list
    if (allItems.length > 0) {
      const lastItem = allItems[allItems.length - 1];
      setLastSelectedKey(lastItem.key || lastItem.name);
    }
  };

  /**
   * Clear all selections.
   */
  const handleClearSelection = () => {
    setSelectedItems(clearSelection());
    setLastSelectedKey(null);
  };

  /**
   * Check if an item is currently selected.
   * 
   * @param {string} itemKey - The key of the item to check
   * @returns {boolean} True if the item is selected
   */
  const checkIsSelected = (itemKey) => {
    return isSelected(itemKey, selectedItems);
  };

  /**
   * Get the count of currently selected items.
   * 
   * @returns {number} Number of selected items
   */
  const getSelectedCount = () => {
    return getSelectionCount(selectedItems);
  };

  /**
   * Get array of currently selected item keys.
   * 
   * @returns {Array<string>} Array of selected item keys
   */
  const getSelected = () => {
    return getSelectedItems(selectedItems);
  };

  /**
   * Handle drag start event for file/folder items.
   * If the dragged item is selected, all selected items are dragged together.
   * Otherwise, only the clicked item is dragged.
   * 
   * Requirement 5.6: Support dragging multiple selected files at once
   * 
   * @param {DragEvent} event - The drag start event
   * @param {string} itemKey - The key of the item being dragged
   */
  const onDragStart = (event, itemKey) => {
    const newState = handleDragStart(event, itemKey, selectedItems, currentFolder);
    setDragState(newState);
  };

  /**
   * Handle drag start event for folder items.
   * Similar to onDragStart but for folders.
   * If the dragged folder is selected, all selected items are dragged together.
   * 
   * Requirement 5.6: Support dragging multiple selected items at once
   * 
   * @param {DragEvent} event - The drag start event
   * @param {string} folderName - The name of the folder being dragged
   */
  const onFolderDragStart = (event, folderName) => {
    const newState = handleDragStart(event, folderName, selectedItems, currentFolder);
    setDragState(newState);
  };

  /**
   * Handle drag end event.
   * Resets the drag state when dragging ends.
   */
  const onDragEnd = () => {
    setDragState(handleDragEnd());
  };

  // ============================================================================
  // External File Drag and Drop Handlers
  // Requirements: 6.1, 6.2
  // - 6.1: Display a visual drop zone indicator when external files are dragged over
  // - 6.2: Upload external files to the current folder when dropped
  // ============================================================================

  /**
   * Handle drag enter event on the FileExplorer container.
   * Detects external file drags and shows the drop zone indicator.
   * 
   * Requirement 6.1: Show visual drop zone indicator when external files are dragged over
   * 
   * @param {DragEvent} event - The drag enter event
   */
  const onExternalDragEnter = (event) => {
    // Only handle external file drags, not internal drags
    if (isExternalFileDrag(event)) {
      const newState = handleExternalDragEnter(event, externalDragState);
      setExternalDragState(newState);
    }
  };

  /**
   * Handle drag over event on the FileExplorer container for external files.
   * Maintains the drop zone indicator while files are being dragged over.
   * 
   * Requirement 6.1: Show visual drop zone indicator
   * 
   * @param {DragEvent} event - The drag over event
   */
  const onExternalDragOver = (event) => {
    // Only handle external file drags
    if (isExternalFileDrag(event)) {
      const newState = handleExternalDragOver(event, externalDragState);
      setExternalDragState(newState);
    }
  };

  /**
   * Handle drag leave event on the FileExplorer container.
   * Hides the drop zone indicator when files are dragged away.
   * 
   * @param {DragEvent} event - The drag leave event
   */
  const onExternalDragLeave = (event) => {
    const newState = handleExternalDragLeave(event, externalDragState, fileExplorerRef.current);
    setExternalDragState(newState);
  };

  /**
   * Upload a single file with retry support.
   * Uses the FileService which creates DynamoDB records and uploads to S3.
   * Uses exponential backoff for retries on failure.
   * 
   * Requirement 6.3: Use existing upload infrastructure with retry support
   * 
   * @param {File} file - The file to upload
   * @param {string} key - The S3 key for the file (used to extract folder path)
   * @param {Function} onProgress - Progress callback (0-100)
   * @param {number} maxRetries - Maximum number of retry attempts
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const uploadFileWithRetry = async (file, key, onProgress, maxRetries = 3) => {
    let lastError = null;
    
    console.log('uploadFileWithRetry called:', {
      fileName: file.name,
      fileSize: file.size,
      key,
      currentFolderId,
      currentFolder
    });
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Use FileService.upload which creates DynamoDB record + uploads to S3
        // Pass the current folder ID for proper parent association
        console.log(`Upload attempt ${attempt + 1} for ${file.name} to folder ${currentFolderId}`);
        await uploadFile(
          file,
          currentFolderId, // Use folder ID, not path
          onProgress
        );
        
        console.log(`Upload successful for ${file.name}`);
        return { success: true };
      } catch (error) {
        lastError = error;
        console.error(`Upload attempt ${attempt + 1} failed for ${file.name}:`, error);
        
        // If we have more retries, wait with exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    return { success: false, error: lastError?.message || 'Upload failed after retries' };
  };

  /**
   * Handle drop event for external files and folders.
   * Uploads the dropped files to the current folder with batch progress tracking.
   * Supports recursive folder upload when folders are dropped.
   * 
   * Requirements: 6.2, 6.3, 6.4, 6.5
   * - 6.2: Upload external files to the current folder when dropped
   * - 6.3: Queue and upload multiple files with progress indication
   * - 6.4: Support dropping folders to upload their entire contents recursively
   * - 6.5: If an upload fails, display an error and allow retry
   * 
   * @param {DragEvent} event - The drop event
   */
  const onExternalFileDrop = async (event) => {
    // Debug: Log drop event details
    console.log('onExternalFileDrop triggered:', {
      types: event.dataTransfer?.types ? Array.from(event.dataTransfer.types) : [],
      files: event.dataTransfer?.files?.length || 0,
      items: event.dataTransfer?.items?.length || 0,
      currentFolderId,
      currentFolder
    });
    
    // Check if this is an external file drop
    if (!isExternalFileDrag(event)) {
      console.log('Not an external file drag, ignoring');
      return;
    }

    // Prevent default browser behavior
    event.preventDefault();
    event.stopPropagation();
    
    console.log('Processing external file drop to folder:', currentFolderId);
    
    // Reset the external drag state
    setExternalDragState(resetExternalDragState());
    
    // Check if the drop contains folders (Requirement 6.4)
    const hasFolder = containsFolder(event);
    
    if (hasFolder) {
      // Handle folder drop with recursive upload
      await handleFolderDrop(event);
    } else {
      // Handle regular file drop
      await handleFileDrop(event);
    }
  };

  /**
   * Handle regular file drop (no folders).
   * Uploads files to the current folder with progress tracking.
   * 
   * Requirements: 6.2, 6.3
   * 
   * @param {DragEvent} event - The drop event
   */
  const handleFileDrop = async (event) => {
    console.log('handleFileDrop called');
    const dropResult = handleExternalDrop(event);
    
    console.log('Drop result:', dropResult);
    
    if (!dropResult || !dropResult.files || dropResult.files.length === 0) {
      console.log('No files in drop result');
      return;
    }

    // Queue dropped files for upload
    const files = Array.from(dropResult.files);
    const totalFiles = files.length;
    
    console.log('Files to upload:', files.map(f => ({ name: f.name, size: f.size })));
    console.log('Uploading to folder ID:', currentFolderId);
    
    // Initialize batch upload state
    setIsBatchUploading(true);
    setBatchUploadProgress({
      completed: 0,
      total: totalFiles,
      currentFile: null,
      currentFileProgress: 0,
      errors: [],
      phase: 'uploading'
    });
    
    const uploadErrors = [];
    
    // Upload files sequentially with progress tracking
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Update current file being uploaded
      setBatchUploadProgress(prev => ({
        ...prev,
        currentFile: file.name,
        currentFileProgress: 0
      }));
      
      // Determine the key based on current folder
      const key = currentFolder 
        ? `${currentFolder}/${file.name}`
        : file.name;
      
      // Upload with retry support and progress callback
      const result = await uploadFileWithRetry(
        file,
        key,
        (progress) => {
          setBatchUploadProgress(prev => ({
            ...prev,
            currentFileProgress: progress
          }));
        }
      );
      
      if (!result.success) {
        uploadErrors.push({ fileName: file.name, error: result.error, file, key });
      }
      
      // Update completed count
      setBatchUploadProgress(prev => ({
        ...prev,
        completed: i + 1,
        errors: uploadErrors
      }));
    }
    
    // Show any errors that occurred (Requirement 6.5)
    if (uploadErrors.length > 0) {
      const errorMessages = uploadErrors.map(e => `${e.fileName}: ${e.error}`).join('\n');
      setError(`Failed to upload ${uploadErrors.length} file(s):\n${errorMessages}`);
    }
    
    // Reset batch upload state after a short delay to show completion
    setTimeout(() => {
      setIsBatchUploading(false);
      setBatchUploadProgress({
        completed: 0,
        total: 0,
        currentFile: null,
        currentFileProgress: 0,
        errors: [],
        phase: 'uploading'
      });
    }, 1500);
    
    // Refresh file list to show uploaded files
    listFilesAndFolders();
  };

  /**
   * Handle folder drop with recursive upload.
   * Reads folder contents recursively and uploads all files maintaining folder structure.
   * 
   * Requirements: 6.4, 6.5
   * - 6.4: Support dropping folders to upload their entire contents recursively
   * - 6.5: If an upload fails, display an error and allow retry
   * 
   * @param {DragEvent} event - The drop event
   */
  const handleFolderDrop = async (event) => {
    try {
      // Show initial status for package bundle detection - start in zipping phase
      // Requirement 9.1 (s3-multipart-upload spec): Display phase indicator
      setIsBatchUploading(true);
      setBatchUploadProgress({
        completed: 0,
        total: 0,
        currentFile: 'Processing dropped items...',
        currentFileProgress: 0,
        errors: [],
        phase: 'zipping' // Start in zipping phase for folder/package processing
      });
      
      // Process dropped items to get all files including from folders
      // Pass a callback to show zip progress for package bundles
      const processResult = await processDroppedItems(event, (bundleName, progress) => {
        setBatchUploadProgress(prev => ({
          ...prev,
          currentFile: `Zipping ${bundleName}... ${progress}%`,
          currentFileProgress: progress,
          phase: 'zipping'
        }));
      });
      
      if (processResult.files.length === 0) {
        setIsBatchUploading(false);
        if (processResult.errors.length > 0) {
          setError(`Failed to read dropped items:\n${processResult.errors.join('\n')}`);
        }
        return;
      }

      const totalFiles = processResult.files.length;
      
      // Show info about package bundles that were zipped
      if (processResult.hasPackageBundle) {
        const zippedFiles = processResult.files.filter(f => f.isZippedBundle);
        if (zippedFiles.length > 0) {
          console.log(`Auto-zipped ${zippedFiles.length} package bundle(s):`, 
            zippedFiles.map(f => `${f.originalBundleName} -> ${f.relativePath}`));
        }
      }
      
      // Initialize folder upload progress state
      setFolderUploadProgress({
        isUploading: true,
        totalFiles,
        completedFiles: 0,
        currentFile: null,
        currentFileProgress: 0,
        folderName: processResult.hasFolder ? 'folder' : (processResult.hasPackageBundle ? 'package' : null),
        errors: [],
        retryQueue: []
      });
      
      // Transition to uploading phase - Requirement 9.2: Seamless transition from zip to upload
      setBatchUploadProgress({
        completed: 0,
        total: totalFiles,
        currentFile: null,
        currentFileProgress: 0,
        errors: [],
        phase: 'uploading' // Transition to uploading phase
      });
      
      const uploadErrors = [];
      const retryQueue = [];
      
      // Upload files sequentially with progress tracking
      for (let i = 0; i < processResult.files.length; i++) {
        const { file, relativePath, isZippedBundle, originalBundleName } = processResult.files[i];
        
        // Update current file being uploaded
        const displayPath = isZippedBundle 
          ? `${originalBundleName} (zipped)` 
          : (relativePath || file.name);
        setBatchUploadProgress(prev => ({
          ...prev,
          currentFile: displayPath,
          currentFileProgress: 0
        }));
        setFolderUploadProgress(prev => ({
          ...prev,
          currentFile: displayPath,
          currentFileProgress: 0
        }));
        
        // Determine the key based on current folder and relative path
        // This maintains the folder structure (Requirement 6.4)
        const key = currentFolder 
          ? `${currentFolder}/${relativePath}`
          : relativePath;
        
        // Upload with retry support and progress callback
        const result = await uploadFileWithRetry(
          file,
          key,
          (progress) => {
            setBatchUploadProgress(prev => ({
              ...prev,
              currentFileProgress: progress
            }));
            setFolderUploadProgress(prev => ({
              ...prev,
              currentFileProgress: progress
            }));
          }
        );
        
        if (!result.success) {
          const errorInfo = { 
            fileName: displayPath, 
            error: result.error, 
            file, 
            key,
            relativePath 
          };
          uploadErrors.push(errorInfo);
          retryQueue.push(errorInfo);
        }
        
        // Update completed count
        setBatchUploadProgress(prev => ({
          ...prev,
          completed: i + 1,
          errors: uploadErrors
        }));
        setFolderUploadProgress(prev => ({
          ...prev,
          completedFiles: i + 1,
          errors: uploadErrors,
          retryQueue
        }));
      }
      
      // Show any errors that occurred (Requirement 6.5)
      if (uploadErrors.length > 0) {
        const errorMessages = uploadErrors.map(e => `${e.fileName}: ${e.error}`).join('\n');
        setError(`Failed to upload ${uploadErrors.length} file(s):\n${errorMessages}`);
      }
      
      // Add any processing errors
      if (processResult.errors.length > 0) {
        const existingError = error || '';
        const processingErrors = processResult.errors.join('\n');
        setError(existingError ? `${existingError}\n\nFolder reading errors:\n${processingErrors}` : `Folder reading errors:\n${processingErrors}`);
      }
      
      // Reset upload state after a short delay to show completion
      setTimeout(() => {
        setIsBatchUploading(false);
        setBatchUploadProgress({
          completed: 0,
          total: 0,
          currentFile: null,
          currentFileProgress: 0,
          errors: [],
          phase: 'uploading'
        });
        setFolderUploadProgress(createFolderUploadProgress());
      }, 1500);
      
      // Refresh file list to show uploaded files
      listFilesAndFolders();
      
    } catch (err) {
      console.error('Error handling folder drop:', err);
      setError(`Failed to process folder drop: ${err.message}`);
      setIsBatchUploading(false);
      setFolderUploadProgress(createFolderUploadProgress());
    }
  };

  /**
   * Retry failed uploads from the folder upload operation.
   * Attempts to re-upload files that failed during the initial upload.
   * 
   * Requirement 6.5: If an upload fails, display an error and allow retry
   * 
   * @param {Array} failedUploads - Array of failed upload info objects
   */
  const retryFailedUploads = async (failedUploads) => {
    if (!failedUploads || failedUploads.length === 0) {
      return;
    }

    const totalFiles = failedUploads.length;
    
    // Initialize retry upload state
    setIsBatchUploading(true);
    setBatchUploadProgress({
      completed: 0,
      total: totalFiles,
      currentFile: null,
      currentFileProgress: 0,
      errors: [],
      phase: 'uploading'
    });
    
    const uploadErrors = [];
    
    // Retry uploads sequentially
    for (let i = 0; i < failedUploads.length; i++) {
      const { file, key, fileName } = failedUploads[i];
      
      // Update current file being uploaded
      setBatchUploadProgress(prev => ({
        ...prev,
        currentFile: fileName,
        currentFileProgress: 0
      }));
      
      // Upload with retry support
      const result = await uploadFileWithRetry(
        file,
        key,
        (progress) => {
          setBatchUploadProgress(prev => ({
            ...prev,
            currentFileProgress: progress
          }));
        }
      );
      
      if (!result.success) {
        uploadErrors.push({ fileName, error: result.error, file, key });
      }
      
      // Update completed count
      setBatchUploadProgress(prev => ({
        ...prev,
        completed: i + 1,
        errors: uploadErrors
      }));
    }
    
    // Show any remaining errors
    if (uploadErrors.length > 0) {
      const errorMessages = uploadErrors.map(e => `${e.fileName}: ${e.error}`).join('\n');
      setError(`Retry failed for ${uploadErrors.length} file(s):\n${errorMessages}`);
    } else {
      setError(''); // Clear error if all retries succeeded
    }
    
    // Reset upload state
    setTimeout(() => {
      setIsBatchUploading(false);
      setBatchUploadProgress({
        completed: 0,
        total: 0,
        currentFile: null,
        currentFileProgress: 0,
        errors: [],
        phase: 'uploading'
      });
    }, 1500);
    
    // Refresh file list
    listFilesAndFolders();
  };

  /**
   * Handle drag over event on a folder.
   * Validates if items can be dropped on the folder and updates drop target state.
   * 
   * Requirement 5.4: Highlight the target folder when dragging over it
   * 
   * @param {DragEvent} event - The drag over event
   * @param {string} folderName - The name of the folder being dragged over
   */
  const onFolderDragOver = (event, folderName) => {
    event.preventDefault();
    // Check if can drop on this folder
    if (canDropOnTarget(dragState.draggedItems, folderName, 'folder')) {
      const newState = handleDragOver(event, folderName, 'folder', dragState);
      setDragState(newState);
    }
  };

  /**
   * Handle drag leave event on a folder.
   * Clears the drop target state when leaving a folder.
   * 
   * @param {DragEvent} event - The drag leave event
   */
  const onFolderDragLeave = (event) => {
    const newState = handleDragLeave(event, dragState);
    setDragState(newState);
  };

  /**
   * Handle drop event on a folder.
   * Moves the dropped items to the target folder.
   * 
   * Requirements: 5.1, 5.5
   * - 5.1: Support drag and drop to move files between folders
   * - 5.5: Show a confirmation dialog if a file with the same name exists in the target folder
   * 
   * @param {DragEvent} event - The drop event
   * @param {string} folderName - The name of the folder being dropped on
   */
  const onFolderDrop = async (event, folderName) => {
    event.preventDefault();
    const dropInfo = handleDrop(event, folderName, 'folder', dragState);
    
    if (dropInfo) {
      // Move the dropped items to the target folder
      await moveItemsToFolder(dropInfo.items, folderName);
    }
    
    setDragState(handleDragEnd());
  };

  /**
   * Move items to a target folder via drag and drop.
   * Similar to handleBulkMove but specifically for drop operations.
   * 
   * Requirements: 5.1, 5.5
   * - 5.1: Support drag and drop to move files between folders
   * - 5.5: Show a confirmation dialog if a file with the same name exists in the target folder
   * 
   * @param {Array<string>} items - Array of item keys to move
   * @param {string} targetFolder - The destination folder name
   */
  const moveItemsToFolder = async (items, targetFolder) => {
    if (!items || items.length === 0) return;
    
    try {
      setError('');
      const allItems = getAllItems();
      const errors = [];
      
      // Construct the full target folder path
      const targetPath = currentFolder ? `${currentFolder}/${targetFolder}` : targetFolder;
      
      // Resolve destination folder ID
      const destinationFolderId = folderIdMap[targetFolder];
      if (!destinationFolderId) {
        setError(`Destination folder "${targetFolder}" not found`);
        return;
      }
      
      // Get list of existing files in target folder to check for conflicts
      let existingFilesInTarget = [];
      try {
        const targetList = await list({ prefix: `${targetPath}/` });
        existingFilesInTarget = targetList.items.map(item => {
          const relativePath = item.key.slice(`${targetPath}/`.length);
          return relativePath.split('/')[0]; // Get just the filename
        });
      } catch (e) {
        // Target folder might not exist yet, which is fine
      }
      
      for (const itemKey of items) {
        const item = allItems.find(i => (i.key || i.name) === itemKey);
        
        try {
          if (item && item.type === 'folder') {
            // Move folder using folder ID (O(1) DynamoDB update)
            const folderId = folderIdMap[itemKey];
            if (!folderId) {
              throw new Error(`Folder ID not found for "${itemKey}"`);
            }
            
            // Requirement 5.4: Check for name conflict and show dialog
            if (existingFilesInTarget.includes(itemKey)) {
              const resolution = await showConflictDialog(itemKey, 'folder', targetFolder);
              
              if (resolution.action === 'skip') {
                continue;
              } else if (resolution.action === 'rename') {
                // For rename, we need to rename first then move
                await renameFolder(folderId, resolution.newName);
              }
              // For 'replace', we proceed with the move
            }
            await moveFolder(folderId, destinationFolderId);
          } else {
            // Move file using file ID (O(1) DynamoDB update)
            const fileId = item?.id;
            const fileName = item?.name || itemKey.split('/').pop();
            
            if (fileId) {
              // Requirement 5.4: Check for name conflict and show dialog
              if (existingFilesInTarget.includes(fileName)) {
                const resolution = await showConflictDialog(fileName, 'file', targetFolder);
                
                if (resolution.action === 'skip') {
                  continue;
                } else if (resolution.action === 'rename') {
                  // Rename first, then move
                  await renameFile(fileId, resolution.newName);
                }
                // For 'replace', we proceed with the move
              }
              
              // Use O(1) DynamoDB move
              await moveFile(fileId, destinationFolderId);
            } else {
              // Legacy file - use S3 copy+delete (slower)
              const sourceKey = itemKey;
              let destinationKey = `${targetPath}/${fileName}`;
              
              // Skip if source and destination are the same
              if (sourceKey !== destinationKey) {
                // Requirement 5.4: Check for name conflict and show dialog
                if (existingFilesInTarget.includes(fileName)) {
                  const resolution = await showConflictDialog(fileName, 'file', targetFolder);
                  
                  if (resolution.action === 'skip') {
                    continue;
                  } else if (resolution.action === 'rename') {
                    destinationKey = `${targetPath}/${resolution.newName}`;
                  }
                  // For 'replace', we proceed with the original destinationKey
                }
                
                // Use Amplify copy for legacy files
                const { copy } = await import('aws-amplify/storage');
                await copy({ source: { key: sourceKey }, destination: { key: destinationKey } });
                await remove({ key: sourceKey });
              }
            }
          }
        } catch (error) {
          errors.push({ item: itemKey, error: error.message });
        }
      }
      
      // Report any failures
      if (errors.length > 0) {
        const errorMessages = errors.map(e => `${e.item}: ${e.error}`).join('\n');
        setError(`Failed to move ${errors.length} item(s):\n${errorMessages}`);
      }
      
      // Clear selection after move
      handleClearSelection();
      
      // Exit Selection Mode after successful operation
      setIsSelectionMode(false);
      
      // Refresh file list to reflect changes
      listFilesAndFolders();
      
    } catch (error) {
      console.error('Error moving items to folder:', error);
      setError(`Failed to move items: ${error.message}`);
    }
  };
  
  /**
   * Show the conflict resolution dialog and wait for user response.
   * Returns a promise that resolves with the user's choice.
   * 
   * Requirement 5.4: IF a file with the same name exists in the destination folder,
   * THEN THE FileExplorer SHALL prompt the user to replace, rename, or cancel
   * 
   * @param {string} itemName - Name of the conflicting item
   * @param {string} itemType - Type of item ('file' or 'folder')
   * @param {string} destinationFolder - Name of the destination folder
   * @returns {Promise<{action: string, newName?: string}>} Resolution object
   */
  const showConflictDialog = (itemName, itemType, destinationFolder) => {
    return new Promise((resolve) => {
      setConflictDialog({
        isOpen: true,
        itemName,
        itemType,
        destinationFolder,
        resolve
      });
    });
  };
  
  /**
   * Handle replace action from conflict dialog.
   * Resolves the pending conflict with 'replace' action.
   */
  const handleConflictReplace = useCallback(() => {
    if (conflictDialog.resolve) {
      conflictDialog.resolve({ action: 'replace' });
    }
    setConflictDialog(prev => ({ ...prev, isOpen: false, resolve: null }));
  }, [conflictDialog.resolve]);
  
  /**
   * Handle rename action from conflict dialog.
   * Resolves the pending conflict with 'rename' action and new name.
   * 
   * @param {string} newName - The new name for the item
   */
  const handleConflictRename = useCallback((newName) => {
    if (conflictDialog.resolve) {
      conflictDialog.resolve({ action: 'rename', newName });
    }
    setConflictDialog(prev => ({ ...prev, isOpen: false, resolve: null }));
  }, [conflictDialog.resolve]);
  
  /**
   * Handle skip action from conflict dialog.
   * Resolves the pending conflict with 'skip' action.
   */
  const handleConflictSkip = useCallback(() => {
    if (conflictDialog.resolve) {
      conflictDialog.resolve({ action: 'skip' });
    }
    setConflictDialog(prev => ({ ...prev, isOpen: false, resolve: null }));
  }, [conflictDialog.resolve]);

  /**
   * Handle bulk delete of selected items.
   * Shows a single confirmation dialog for all items, then deletes them sequentially
   * with progress tracking. Reports any failures at the end.
   * 
   * Requirements: 8.5, 8.7
   * - 8.5: Show single confirmation dialog for all items
   * - 8.7: Delete items sequentially with progress, report any failures at end
   * 
   * @returns {Promise<void>}
   */
  const handleDeleteSelected = async () => {
    const selected = getSelected();
    if (selected.length === 0) return;
    
    // Requirement 8.5: Show single confirmation dialog for all items
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selected.length} item(s)?\n\n` +
      `⚠️ WARNING: This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      setIsBulkDeleting(true);
      setBulkDeleteProgress({ completed: 0, total: selected.length, currentItem: null });
      setError('');
      
      const errors = [];
      const allItems = getAllItems();
      
      // Requirement 8.7: Delete items sequentially with progress
      for (let i = 0; i < selected.length; i++) {
        const itemKey = selected[i];
        const item = allItems.find(item => (item.key || item.name) === itemKey);
        
        // Update progress before each deletion
        setBulkDeleteProgress({
          completed: i,
          total: selected.length,
          currentItem: itemKey
        });
        
        try {
          if (item && item.type === 'folder') {
            // Delete folder using folder ID (DynamoDB)
            const folderId = folderIdMap[itemKey];
            if (folderId) {
              await deleteFolder(folderId);
            } else {
              // Folder ID not found - this shouldn't happen for DynamoDB-managed folders
              throw new Error(`Folder ID not found for "${itemKey}"`);
            }
          } else {
            // Delete file using file ID (DynamoDB) or legacy key (S3)
            const fileId = item?.id;
            await deleteFile(fileId || itemKey);
          }
        } catch (error) {
          // Collect errors for reporting at end
          errors.push({ item: itemKey, error: error.message });
        }
      }
      
      // Update progress to show completion
      setBulkDeleteProgress({
        completed: selected.length,
        total: selected.length,
        currentItem: null
      });
      
      // Requirement 8.7: Report any failures at end
      if (errors.length > 0) {
        const errorMessages = errors.map(e => `${e.item}: ${e.error}`).join('\n');
        setError(`Failed to delete ${errors.length} item(s):\n${errorMessages}`);
      }
      
      // Clear selection after completion
      handleClearSelection();
      
      // Exit Selection Mode after successful bulk operation
      setIsSelectionMode(false);
      
      // Refresh file list to reflect changes
      listFilesAndFolders();
      
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteProgress({ completed: 0, total: 0, currentItem: null });
    }
  };

  /**
   * Handle bulk move of selected items.
   * Opens the folder picker modal for destination selection.
   * 
   * Requirements: 8.6, 8.7
   * - 8.6: Show folder picker modal for destination selection
   * - 8.7: Move items sequentially with progress, handle name conflicts
   */
  const handleMoveSelected = async () => {
    const selected = getSelected();
    if (selected.length === 0) return;
    
    // Fetch all folders for the picker
    const folders = await fetchAllFolders();
    setAllFolders(folders);
    setShowBulkMoveModal(true);
  };

  /**
   * Handle bulk move operation to the selected destination folder.
   * Moves items sequentially with progress tracking.
   * For files: copy to destination, delete original.
   * For folders: use renameFolder from FolderService (which handles recursive move).
   * 
   * Requirements: 8.6, 8.7
   * - 8.6: Show folder picker modal for destination selection
   * - 8.7: Move items sequentially with progress, handle name conflicts
   * 
   * @param {Object} destinationFolder - The destination folder object with { path, id, name }
   */
  const handleBulkMove = async (destinationFolder) => {
    const selected = getSelected();
    if (selected.length === 0) return;
    
    try {
      setIsBulkMoving(true);
      setBulkMoveProgress({ completed: 0, total: selected.length, currentItem: null });
      setError('');
      
      const errors = [];
      const allItems = getAllItems();
      
      // Get destination folder ID from the folder object
      const destinationFolderId = destinationFolder?.id || PUBLIC_FOLDER_ID;
      const destinationPath = destinationFolder?.path || '';
      
      // Move items sequentially with progress (Requirement 8.7)
      for (let i = 0; i < selected.length; i++) {
        const itemKey = selected[i];
        const item = allItems.find(item => (item.key || item.name) === itemKey);
        
        // Update progress before each move
        setBulkMoveProgress({
          completed: i,
          total: selected.length,
          currentItem: itemKey
        });
        
        try {
          if (item && item.type === 'folder') {
            // Move folder using folder ID (O(1) DynamoDB update)
            const folderId = folderIdMap[itemKey];
            if (folderId) {
              await moveFolder(folderId, destinationFolderId);
            } else {
              // Folder ID not found - this shouldn't happen for DynamoDB-managed folders
              throw new Error(`Folder ID not found for "${itemKey}"`);
            }
          } else {
            // Move file using file ID (O(1) DynamoDB update)
            const fileId = item?.id;
            
            if (fileId) {
              await moveFile(fileId, destinationFolderId);
            } else {
              // Legacy file - use S3 copy+delete (slower)
              const fileName = item?.name || itemKey.split('/').pop();
              const sourceKey = itemKey;
              const destinationKey = destinationPath 
                ? `${destinationPath}/${fileName}` 
                : fileName;
              
              // Skip if source and destination are the same
              if (sourceKey !== destinationKey) {
                // Use Amplify copy for legacy files
                const { copy } = await import('aws-amplify/storage');
                await copy({ source: { key: sourceKey }, destination: { key: destinationKey } });
                await remove({ key: sourceKey });
              }
            }
          }
        } catch (error) {
          // Collect errors for reporting at end (Requirement 8.7)
          errors.push({ item: itemKey, error: error.message });
        }
      }
      
      // Update progress to show completion
      setBulkMoveProgress({
        completed: selected.length,
        total: selected.length,
        currentItem: null
      });
      
      // Report any failures at end (Requirement 8.7)
      if (errors.length > 0) {
        const errorMessages = errors.map(e => `${e.item}: ${e.error}`).join('\n');
        setError(`Failed to move ${errors.length} item(s):\n${errorMessages}`);
      }
      
      // Clear selection after completion
      handleClearSelection();
      
      // Exit Selection Mode after successful bulk operation
      setIsSelectionMode(false);
      
      // Refresh file list to reflect changes
      listFilesAndFolders();
      
      // Close the modal
      setShowBulkMoveModal(false);
      
    } finally {
      setIsBulkMoving(false);
      setBulkMoveProgress({ completed: 0, total: 0, currentItem: null });
    }
  };

  /**
   * Handle context menu action for bulk move of selected items.
   * Closes the menu and opens the bulk move modal.
   * 
   * Requirement 11.4: Bulk action options in multi-selection context menu
   */
  const handleContextMenuBulkMove = useCallback(async () => {
    handleCloseContextMenu();
    // Trigger the bulk move modal
    const folders = await fetchAllFolders();
    setAllFolders(folders);
    setShowBulkMoveModal(true);
  }, [handleCloseContextMenu]);

  /**
   * Handle context menu action for bulk delete of selected items.
   * Closes the menu and triggers the bulk delete confirmation.
   * 
   * Requirement 11.4: Bulk action options in multi-selection context menu
   */
  const handleContextMenuBulkDelete = useCallback(() => {
    handleCloseContextMenu();
    // Trigger the bulk delete operation
    handleDeleteSelected();
  }, [handleCloseContextMenu, handleDeleteSelected]);

  /**
   * Get context menu items for multi-selection right-click.
   * Returns array of bulk action menu items with labels, icons, and click handlers.
   * Shows the count of selected items in the menu labels.
   * 
   * Requirement 11.4: WHEN multiple items are selected and user right-clicks,
   * THE Context_Menu SHALL display bulk action options
   * 
   * @returns {Array} Array of menu item objects
   */
  const getMultiSelectionContextMenuItems = useCallback(() => {
    const isDisabled = isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving;
    const count = getSelectionCount(selectedItems);
    
    return [
      {
        label: `Move Selected (${count} items)`,
        icon: <MoveIcon size={16} />,
        onClick: handleContextMenuBulkMove,
        disabled: isDisabled
      },
      { divider: true },
      {
        label: `Delete Selected (${count} items)`,
        icon: <DeleteIcon size={16} />,
        onClick: handleContextMenuBulkDelete,
        disabled: isDisabled
      }
    ];
  }, [
    isRenaming,
    isFolderRenaming,
    isFolderDeleting,
    isBulkDeleting,
    isBulkMoving,
    selectedItems,
    handleContextMenuBulkMove,
    handleContextMenuBulkDelete
  ]);

  /**
   * Check if the context menu should show multi-selection options.
   * Returns true if multiple items are selected AND the right-clicked item
   * is part of the current selection.
   * 
   * Requirement 11.4: Detect when multiple items selected
   * 
   * @returns {boolean} True if multi-selection context menu should be shown
   */
  const shouldShowMultiSelectionMenu = useCallback(() => {
    const count = getSelectionCount(selectedItems);
    if (count < 2) return false;
    
    // Check if the right-clicked item is part of the selection
    if (!contextMenu.targetItem) return false;
    
    // Get the key of the right-clicked item
    let targetKey;
    if (contextMenu.targetType === 'file') {
      targetKey = contextMenu.targetItem.key;
    } else if (contextMenu.targetType === 'folder') {
      targetKey = contextMenu.targetItem; // For folders, targetItem is the folder name
    } else {
      return false;
    }
    
    return isSelected(targetKey, selectedItems);
  }, [contextMenu.targetItem, contextMenu.targetType, selectedItems]);

  /**
   * Handle checkbox click for item selection.
   * Uses Ctrl+click behavior to toggle selection without affecting other selections.
   * 
   * Requirement 8.4: Add checkbox to each item (files and folders)
   * 
   * @param {string} itemKey - The key of the item whose checkbox was clicked
   * @param {Object} event - The click event
   */
  const handleCheckboxClick = (itemKey, event) => {
    // Stop propagation to prevent triggering the item click handler
    event.stopPropagation();
    
    // Use Ctrl+click behavior for checkbox toggle
    const newSelection = handleCtrlClick(itemKey, selectedItems);
    setSelectedItems(newSelection);
    setLastSelectedKey(itemKey);
  };

  /**
   * Handle folder deletion.
   * Shows a confirmation dialog, then calls FolderService.deleteFolder with progress callback.
   * Displays progress during deletion and handles partial failures with error reporting.
   * 
   * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5
   * - 1.1: Display a confirmation dialog warning that all contents will be deleted
   * - 1.2: Recursively delete all files and subfolders within the target folder
   * - 1.3: Display a progress indicator showing deletion status
   * - 1.4: Display an error message and report which files could not be deleted
   * - 1.5: Refresh the current view to reflect the changes
   * 
   * @param {string} folderName - The name of the folder to delete
   */
  const handleFolderDelete = async (folderName) => {
    if (!folderName) {
      return;
    }

    // Requirement 1.1: Show confirmation dialog with warning about contents
    const confirmed = window.confirm(
      `Are you sure you want to delete the folder "${folderName}"?\n\n` +
      `⚠️ WARNING: This will permanently delete all files and subfolders within this folder.\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingFolderName(folderName);
      setIsFolderDeleting(true);
      setFolderDeleteProgress({ completed: 0, total: 0, currentItem: null });
      setError('');

      // Get the folder ID from the folderIdMap (DynamoDB requires ID, not path)
      const folderId = folderIdMap[folderName];
      if (!folderId) {
        throw new Error(`Folder ID not found for "${folderName}". The folder may have been deleted or moved.`);
      }

      // Progress callback to update the UI during deletion (Requirement 1.3)
      const onProgress = (progress) => {
        setFolderDeleteProgress({
          completed: progress.completed,
          total: progress.total,
          currentItem: progress.currentItem
        });
      };

      // Call FolderService to delete the folder by ID (Requirement 1.2)
      await deleteFolder(folderId, onProgress, true); // recursive=true to delete contents

      // Clear the deletion state
      setDeletingFolderName(null);
      setIsFolderDeleting(false);
      setFolderDeleteProgress({ completed: 0, total: 0, currentItem: null });

      // Refresh the file list to reflect the changes (Requirement 1.5)
      listFilesAndFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
      // Requirement 1.4: Display an error message and report which files could not be deleted
      setError(`Failed to delete folder: ${error.message}`);
      setIsFolderDeleting(false);
      setDeletingFolderName(null);
      setFolderDeleteProgress({ completed: 0, total: 0, currentItem: null });
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Please enter a folder name.');
      return;
    }
    
    try {
      // Use DynamoDB API to create folder
      const parentFolderId = currentFolderId || PUBLIC_FOLDER_ID;
      await createFolderApi(newFolderName.trim(), parentFolderId);
      
      // Reset the input and refresh the file list
      setNewFolderName('');
      setShowNewFolderInput(false);
      listFilesAndFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      setError('Failed to create folder. Please try again.');
    }
  };

  const navigateToFolder = (folder) => {
    // Get the folder ID from the map before navigating
    const folderId = folderIdMap[folder];
    // Use ref to get current path to avoid stale closure issues
    const currentPath = currentFolderRef.current;
    const newPath = currentPath ? `${currentPath}/${folder}` : folder;
    console.log('navigateToFolder:', { folder, folderId, currentPath, newPath, folderIdMap });
    
    // Update ref immediately to prevent stale closure issues
    currentFolderRef.current = newPath;
    
    // Update state
    setCurrentFolder(newPath);
    setCurrentFolderId(folderId || null);
    
    // Clear selection when navigating to a new folder
    handleClearSelection();
    // Clear search when navigating to a new folder
    setSearchQuery('');
    
    // Directly call listFilesAndFolders with the new values to avoid stale state issues
    // The useEffect will also trigger, but the loading ref will prevent duplicate calls
    if (folderId) {
      isLoadingRef.current = false; // Reset to allow this call
      listFilesAndFolders(folderId, newPath);
    }
  };

  /**
   * Handle breadcrumb navigation - navigates to the specified path.
   * Property 7: Breadcrumb Navigation
   * For any breadcrumb segment clicked, the resulting current path shall equal
   * the path represented by that segment and all preceding segments.
   * 
   * @param {string} path - The path to navigate to
   */
  const handleBreadcrumbNavigate = async (path) => {
    console.log('handleBreadcrumbNavigate:', { path });
    
    // Update ref immediately to prevent stale closure issues
    currentFolderRef.current = path;
    
    setCurrentFolder(path);
    
    // Resolve the folder ID from the path (empty path = public folder)
    let folderId;
    if (!path) {
      folderId = PUBLIC_FOLDER_ID;
    } else {
      folderId = await resolveFolderIdFromPath(path);
    }
    
    setCurrentFolderId(folderId || PUBLIC_FOLDER_ID);
    
    // Clear selection when navigating via breadcrumbs
    handleClearSelection();
    // Clear search when navigating via breadcrumbs
    setSearchQuery('');
    
    // Directly call listFilesAndFolders with the resolved values
    if (folderId) {
      isLoadingRef.current = false; // Reset to allow this call
      listFilesAndFolders(folderId, path);
    }
  };

  /**
   * Handle drag-and-drop onto breadcrumb segments.
   * Moves dropped items to the target folder path.
   * 
   * @param {string} targetPath - The destination folder path
   * @param {Array} items - The items being dropped
   */
  const handleBreadcrumbDrop = async (targetPath, items) => {
    // For now, we'll handle single file moves
    // This can be extended for bulk operations later
    if (!items || items.length === 0) return;
    
    // Resolve destination folder ID from path
    let destinationFolderId = PUBLIC_FOLDER_ID;
    if (targetPath) {
      destinationFolderId = await resolveFolderIdFromPath(targetPath);
      if (!destinationFolderId) {
        setError(`Destination folder "${targetPath}" not found`);
        return;
      }
    }
    
    for (const item of items) {
      if (item.type === 'file') {
        try {
          // Check if file has an ID (DynamoDB-managed) or is legacy (S3-only)
          if (item.id) {
            // Use O(1) DynamoDB move
            await moveFile(item.id, destinationFolderId);
          } else if (item.key) {
            // Legacy file - use S3 copy+delete (slower)
            const fileName = item.name || item.key.split('/').pop();
            const sourceKey = item.key;
            const destinationKey = targetPath 
              ? `${targetPath}/${fileName}` 
              : fileName;
            
            // Skip if source and destination are the same
            if (sourceKey === destinationKey) continue;
            
            // Use Amplify copy for legacy files
            const { copy } = await import('aws-amplify/storage');
            await copy({ source: { key: sourceKey }, destination: { key: destinationKey } });
            await remove({ key: sourceKey });
          }
        } catch (error) {
          console.error('Error moving file via breadcrumb drop:', error);
          setError(`Failed to move ${item.name || 'file'}. Please try again.`);
        }
      } else if (item.type === 'folder') {
        // Handle folder move via breadcrumb drop using folder ID
        try {
          const folderName = item.name || item.key?.split('/').pop();
          
          // Get folder ID from the folderIdMap (for items in current directory)
          const folderId = folderIdMap[folderName] || item.id;
          
          if (!folderId) {
            console.error(`Folder ID not found for "${folderName}"`);
            setError(`Failed to move folder "${folderName}": folder ID not found`);
            continue;
          }
          
          // Use moveFolder which does O(1) DynamoDB update
          await moveFolder(folderId, destinationFolderId);
        } catch (error) {
          console.error('Error moving folder via breadcrumb drop:', error);
          setError(`Failed to move ${item.name || 'folder'}. Please try again.`);
        }
      }
    }
    
    // Refresh the file list after all moves
    listFilesAndFolders();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className={`container ${shouldShowDropZone(externalDragState) ? 'external-drag-active' : ''} ${isSelectionMode ? 'file-explorer--selection-mode' : 'file-explorer--normal-mode'}`}
      ref={fileExplorerRef}
      onDragEnter={onExternalDragEnter}
      onDragOver={onExternalDragOver}
      onDragLeave={onExternalDragLeave}
      onDrop={onExternalFileDrop}
    >
      {/* External File Drop Zone Overlay - Requirement 6.1 */}
      {shouldShowDropZone(externalDragState) && (
        <div 
          className="external-drop-zone-overlay" 
          data-testid="external-drop-zone"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer) {
              e.dataTransfer.dropEffect = 'copy';
            }
          }}
          onDragLeave={(e) => {
            // Only reset if leaving the overlay entirely (not entering a child)
            if (e.relatedTarget === null || !e.currentTarget.contains(e.relatedTarget)) {
              setExternalDragState(resetExternalDragState());
            }
          }}
          onDrop={onExternalFileDrop}
        >
          <div className="external-drop-zone-content">
            <div className="external-drop-zone-icon"><UploadIcon size={48} /></div>
            <div className="external-drop-zone-text">Drop files here to upload</div>
            <div className="external-drop-zone-subtext">
              Files will be uploaded to {currentFolder || 'root folder'}
            </div>
          </div>
        </div>
      )}
      
      {/* Batch Upload Progress Overlay - Requirement 6.3 */}
      {/* Requirements 9.1, 9.2, 9.3 (s3-multipart-upload spec): Phase tracking for zip+upload flow */}
      {isBatchUploading && (
        <div className="batch-upload-overlay" data-testid="batch-upload-progress">
          <div className="batch-upload-content">
            <div className="batch-upload-header">
              <div className="batch-upload-icon"><UploadIcon size={32} /></div>
              <div className="batch-upload-title">
                {batchUploadProgress.phase === 'zipping' ? 'Preparing Files' : 'Uploading Files'}
              </div>
              {/* Phase indicator - Requirement 9.1: Display phase indicator */}
              <div className="batch-upload-phase" data-testid="upload-phase-indicator">
                {batchUploadProgress.phase === 'zipping' ? '📦 Zipping...' : '☁️ Uploading...'}
              </div>
            </div>
            
            {/* Overall batch progress */}
            <div className="batch-upload-overall">
              <div className="batch-upload-count">
                {batchUploadProgress.phase === 'zipping' 
                  ? 'Preparing package bundle...'
                  : `${batchUploadProgress.completed} of ${batchUploadProgress.total} files uploaded`
                }
              </div>
              <div className="progress-bar batch-progress-bar">
                <div 
                  className="progress-bar-fill" 
                  style={{ 
                    width: batchUploadProgress.total > 0 
                      ? `${Math.round((batchUploadProgress.completed / batchUploadProgress.total) * 100)}%` 
                      : '0%' 
                  }}
                ></div>
              </div>
            </div>
            
            {/* Current file progress */}
            {batchUploadProgress.currentFile && (
              <div className="batch-upload-current">
                <div className="current-file-name">
                  Current: {batchUploadProgress.currentFile}
                </div>
                <div className="progress-bar current-file-progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${batchUploadProgress.currentFileProgress}%` }}
                  ></div>
                </div>
                <div className="current-file-percent">
                  {batchUploadProgress.currentFileProgress}%
                </div>
              </div>
            )}
            
            {/* Error summary if any */}
            {batchUploadProgress.errors.length > 0 && (
              <div className="batch-upload-errors">
                <div className="batch-upload-error-count">
                  ⚠️ {batchUploadProgress.errors.length} file(s) failed
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <h2>Files</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button className="dismiss-button" onClick={() => setError('')}>×</button>
        </div>
      )}
      
      {!currentFolder ? (
        <div>
          <Breadcrumbs 
            path={currentFolder}
            onNavigate={handleBreadcrumbNavigate}
            onDrop={handleBreadcrumbDrop}
          />
          
          {/* Search Bar - Requirement 7.1: Display a search input field prominently */}
          {/* Requirement 7.3: Global search toggle to search across all folders */}
          {/* Sort Controls - Requirements 9.1, 9.2, 9.5 */}
          <div className="toolbar-container">
            <div className="search-bar-container">
              <SearchBar
                value={searchQuery}
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                placeholder="Search files and folders..."
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
                
                
                
              />
            </div>
            
            {/* Sort Controls - Requirements 9.1, 9.2, 9.5 */}
            <div className="sort-controls" data-testid="sort-controls">
              <label className="sort-label">Sort by:</label>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => handleSortByChange(e.target.value)}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
                data-testid="sort-field-select"
                aria-label="Sort by field"
              >
                <option value="name">Name</option>
                <option value="date">Date Modified</option>
                <option value="size">Size</option>
              </select>
              <button
                className={`sort-order-button ${sortOrder}`}
                onClick={handleSortOrderToggle}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
                data-testid="sort-order-button"
                aria-label={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
                title={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'} - click to toggle`}
              >
                <span className="sort-order-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                <span className="sort-order-text">{sortOrder === 'asc' ? 'Asc' : 'Desc'}</span>
              </button>
            </div>
            
            {/* View Mode Toggle - Requirements 10.1, 10.3, 10.5 */}
            <div className="view-mode-controls" data-testid="view-mode-controls">
              <button
                className={`view-mode-button ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('list')}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving}
                data-testid="view-mode-list-button"
                aria-label="List view"
                title="List view"
              >
                <span className="view-mode-icon">☰</span>
              </button>
              <button
                className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('grid')}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving}
                data-testid="view-mode-grid-button"
                aria-label="Grid view"
                title="Grid view"
              >
                <span className="view-mode-icon">▦</span>
              </button>
            </div>
            
            {/* Selection Mode Toggle - Requirement 3.1 (selection-mode-toggle spec) */}
            {/* 3.1: THE FileExplorer SHALL display a Selection_Toggle button in the toolbar area */}
            <SelectionToggle
              isSelectionMode={isSelectionMode}
              onToggle={handleSelectionModeToggle}
              disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
            />
          </div>
          
          {/* Global Search Results - Requirements 7.3, 7.4, 7.5 */}
          {searchQuery && (
            <div className="global-search-results" data-testid="global-search-results">
              {isGlobalSearchLoading ? (
                <div className="global-search-loading" data-testid="global-search-loading">
                  <p>Searching all folders...</p>
                </div>
              ) : globalSearchResults.length > 0 ? (
                <>
                  <div className="global-search-header">
                    <h3>Search Results ({globalSearchResults.length})</h3>
                  </div>
                  <ul className="global-search-list">
                    {globalSearchResults.map((result, index) => (
                      <li 
                        key={`${result.type}-${result.key}-${index}`}
                        className={`global-search-item ${result.type === 'folder' ? 'folder-result' : 'file-result'}`}
                        onClick={() => navigateToSearchResult(result)}
                        data-testid="global-search-result-item"
                      >
                        <span className="result-icon">
                          {result.type === 'folder' ? <FolderIcon size={16} /> : <FileIcon size={16} />}
                        </span>
                        <div className="result-info">
                          <span className="result-name">{result.name}</span>
                          <span className="result-path" data-testid="result-folder-path">
                            in {result.folderPath}
                          </span>
                        </div>
                        {result.type === 'file' && result.size && (
                          <span className="result-size">{formatFileSize(result.size)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          )}
          
          {/* Bulk Action Toolbar - shows when in Selection Mode with items selected */}
          {/* Requirement 8.4: Show selection count and bulk action buttons */}
          {/* Requirement 5.3, 5.4: Only show toolbar in Selection Mode with selections */}
          {!searchQuery && isSelectionMode && getSelectedCount() > 0 && (
            <BulkActionToolbar
              selectedCount={getSelectedCount()}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onDeleteSelected={handleDeleteSelected}
              onMoveSelected={handleMoveSelected}
              disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
            />
          )}
          
          {/* Hide folder actions and content when showing global search results */}
          {(!isGlobalSearch || !searchQuery) && (
            <>
              <div className="folder-actions">
                <button 
                  className="new-folder-button"
                  onClick={() => setShowNewFolderInput(!showNewFolderInput)}
                >
                  {showNewFolderInput ? 'Cancel' : 'New Folder'}
                </button>
              </div>
              
              {showNewFolderInput && (
                <div className="new-folder-input">
                  <input
                    type="text"
                    placeholder="Enter folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                  <button onClick={createFolder}>Create</button>
                </div>
              )}
            </>
          )}
          
          {/* No results message - shown when search has no matches */}
          {hasNoSearchResults && (
            <div className="no-results-message" data-testid="no-results-message">
              <p>No results found for "{searchQuery}"</p>
            </div>
          )}
          
          {/* Hide folder grid when showing global search results */}
          {/* Requirement 11.3: Right-click on empty space shows context menu */}
          {(!isGlobalSearch || !searchQuery) && (
            <div 
              className="folder-grid"
              onContextMenu={handleEmptySpaceContextMenu}
              data-testid="folder-grid-container"
            >
              {filteredFolderStructure.map((folder) => (
                <div 
                  key={folder} 
                  className={`folder ${checkIsSelected(folder) ? 'selected' : ''} ${isDropTarget(folder, dragState) ? 'drop-target' : ''}`}
                  onClick={(e) => handleFolderClick(folder, e)}
                  onContextMenu={(e) => handleFolderContextMenu(e, folder)}
                  onDragOver={(e) => onFolderDragOver(e, folder)}
                  onDragLeave={onFolderDragLeave}
                  onDrop={(e) => onFolderDrop(e, folder)}
                  data-testid={`folder-item-${folder.replace(/\s+/g, '-')}`}
                >
                  {renamingFolderName === folder ? (
                    <div className="folder-rename-container">
                      <InlineRenameInput
                        currentName={folder}
                        onSubmit={(newName) => handleFolderRename(folder, newName)}
                        onCancel={handleCancelFolderRename}
                        existingNames={folders.filter(f => f !== folder)}
                        placeholder="Enter folder name"
                      />
                      {isFolderRenaming && (
                        <div className="folder-rename-progress">
                          <div className="progress-bar">
                            <div 
                              className="progress-bar-fill" 
                              style={{ 
                                width: folderRenameProgress.total > 0 
                                  ? `${Math.round((folderRenameProgress.completed / folderRenameProgress.total) * 100)}%` 
                                  : '0%' 
                              }}
                            ></div>
                          </div>
                          <span className="rename-progress-text">
                            {folderRenameProgress.total > 0 
                              ? `Renaming... ${folderRenameProgress.completed}/${folderRenameProgress.total}`
                              : 'Preparing...'}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : deletingFolderName === folder ? (
                    <div className="folder-delete-container">
                      <h3 className="folder-title">{folder}</h3>
                      <div className="folder-delete-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-bar-fill delete-progress-fill" 
                            style={{ 
                              width: folderDeleteProgress.total > 0 
                                ? `${Math.round((folderDeleteProgress.completed / folderDeleteProgress.total) * 100)}%` 
                                : '0%' 
                            }}
                          ></div>
                        </div>
                        <span className="delete-progress-text">
                          {folderDeleteProgress.total > 0 
                            ? `Deleting... ${folderDeleteProgress.completed}/${folderDeleteProgress.total}`
                            : 'Preparing to delete...'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Checkbox for folder selection - Requirement 8.4 */}
                      {/* Conditionally render checkbox only in Selection Mode - Requirements 1.4, 4.1 */}
                      {isSelectionMode && (
                        <input
                          type="checkbox"
                          className="item-checkbox folder-checkbox"
                          checked={checkIsSelected(folder)}
                          onChange={(e) => handleCheckboxClick(folder, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div className="folder-content">
                        <h3 className="folder-title">{folder}</h3>
                        <p>Click to open</p>
                      </div>
                      {/* Inline hover actions for folders - Requirement 12.3 */}
                      {/* 12.3: Actions: Rename, Delete */}
                      {/* Requirements 2.1, 2.3 (selection-mode-toggle): Only show in Normal Mode, no Open button */}
                      {!isSelectionMode && (
                        <div className="folder-item-actions" data-testid={`folder-inline-actions-${folder.replace(/\s+/g, '-')}`}>
                          <button 
                            className="inline-action-button rename-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingFolderName(folder);
                            }}
                            disabled={isFolderRenaming || isFolderDeleting || renamingFolderName !== null}
                            title="Rename"
                            aria-label="Rename folder"
                            data-testid={`folder-inline-action-rename-${folder.replace(/\s+/g, '-')}`}
                          >
                            <EditIcon size={14} />
                          </button>
                          <button 
                            className="inline-action-button delete-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFolderDelete(folder);
                            }}
                            disabled={isFolderRenaming || isFolderDeleting || renamingFolderName !== null}
                            title="Delete"
                            aria-label="Delete folder"
                            data-testid={`folder-inline-action-delete-${folder.replace(/\s+/g, '-')}`}
                          >
                            <DeleteIcon size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <Breadcrumbs 
            path={currentFolder}
            onNavigate={handleBreadcrumbNavigate}
            onDrop={handleBreadcrumbDrop}
          />
          
          {/* Search Bar - Requirement 7.1: Display a search input field prominently */}
          {/* Requirement 7.3: Global search toggle to search across all folders */}
          {/* Sort Controls - Requirements 9.1, 9.2, 9.5 */}
          <div className="toolbar-container">
            <div className="search-bar-container">
              <SearchBar
                value={searchQuery}
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                placeholder="Search files and folders..."
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
                
                
                
              />
            </div>
            
            {/* Sort Controls - Requirements 9.1, 9.2, 9.5 */}
            <div className="sort-controls" data-testid="sort-controls">
              <label className="sort-label">Sort by:</label>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => handleSortByChange(e.target.value)}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
                data-testid="sort-field-select"
                aria-label="Sort by field"
              >
                <option value="name">Name</option>
                <option value="date">Date Modified</option>
                <option value="size">Size</option>
              </select>
              <button
                className={`sort-order-button ${sortOrder}`}
                onClick={handleSortOrderToggle}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
                data-testid="sort-order-button"
                aria-label={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
                title={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'} - click to toggle`}
              >
                <span className="sort-order-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                <span className="sort-order-text">{sortOrder === 'asc' ? 'Asc' : 'Desc'}</span>
              </button>
            </div>
            
            {/* View Mode Toggle - Requirements 10.1, 10.3, 10.5 */}
            <div className="view-mode-controls" data-testid="view-mode-controls">
              <button
                className={`view-mode-button ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('list')}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving}
                data-testid="view-mode-list-button"
                aria-label="List view"
                title="List view"
              >
                <span className="view-mode-icon">☰</span>
              </button>
              <button
                className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('grid')}
                disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving}
                data-testid="view-mode-grid-button"
                aria-label="Grid view"
                title="Grid view"
              >
                <span className="view-mode-icon">▦</span>
              </button>
            </div>
            
            {/* Selection Mode Toggle - Requirement 3.1 (selection-mode-toggle spec) */}
            {/* 3.1: THE FileExplorer SHALL display a Selection_Toggle button in the toolbar area */}
            <SelectionToggle
              isSelectionMode={isSelectionMode}
              onToggle={handleSelectionModeToggle}
              disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
            />
          </div>
          
          {/* Global Search Results - Requirements 7.3, 7.4, 7.5 */}
          {searchQuery && (
            <div className="global-search-results" data-testid="global-search-results">
              {isGlobalSearchLoading ? (
                <div className="global-search-loading" data-testid="global-search-loading">
                  <p>Searching all folders...</p>
                </div>
              ) : globalSearchResults.length > 0 ? (
                <>
                  <div className="global-search-header">
                    <h3>Search Results ({globalSearchResults.length})</h3>
                  </div>
                  <ul className="global-search-list">
                    {globalSearchResults.map((result, index) => (
                      <li 
                        key={`${result.type}-${result.key}-${index}`}
                        className={`global-search-item ${result.type === 'folder' ? 'folder-result' : 'file-result'}`}
                        onClick={() => navigateToSearchResult(result)}
                        data-testid="global-search-result-item"
                      >
                        <span className="result-icon">
                          {result.type === 'folder' ? <FolderIcon size={16} /> : <FileIcon size={16} />}
                        </span>
                        <div className="result-info">
                          <span className="result-name">{result.name}</span>
                          <span className="result-path" data-testid="result-folder-path">
                            in {result.folderPath}
                          </span>
                        </div>
                        {result.type === 'file' && result.size && (
                          <span className="result-size">{formatFileSize(result.size)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          )}
          
          {/* Bulk Action Toolbar - shows when in Selection Mode with items selected */}
          {/* Requirement 8.4: Show selection count and bulk action buttons */}
          {/* Requirement 5.3, 5.4: Only show toolbar in Selection Mode with selections */}
          {!searchQuery && isSelectionMode && getSelectedCount() > 0 && (
            <BulkActionToolbar
              selectedCount={getSelectedCount()}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onDeleteSelected={handleDeleteSelected}
              onMoveSelected={handleMoveSelected}
              disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
            />
          )}
          
          {/* Hide folder content when showing global search results */}
          {(!isGlobalSearch || !searchQuery) && (
            <>
              <div className="folder-header">
                <h2>{currentFolder.split('/').pop() || currentFolder}</h2>
            <button 
              className="new-folder-button"
              onClick={() => setShowNewFolderInput(!showNewFolderInput)}
            >
              {showNewFolderInput ? 'Cancel' : 'New Folder'}
            </button>
          </div>
          
          {showNewFolderInput && (
            <div className="new-folder-input">
              <input
                type="text"
                placeholder="Enter folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
              />
              <button onClick={createFolder}>Create</button>
            </div>
          )}
          
          {/* No results message - shown when search has no matches */}
          {hasNoSearchResults && (
            <div className="no-results-message" data-testid="no-results-message">
              <p>No results found for "{searchQuery}"</p>
            </div>
          )}
          
          <div className="upload-area">
            <h3>Upload Files</h3>
            <p className="upload-info">Select a file and click the Upload button</p>
            <div className="upload-controls">
              <input 
                id="file-upload"
                type="file" 
                onChange={handleFileSelect} 
                disabled={uploading}
              />
              <button 
                className="upload-button" 
                onClick={startUpload} 
                disabled={!selectedFile || uploading}
              >
                Upload
              </button>
            </div>
            {selectedFile && (
              <div className="selected-file">
                <p>Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</p>
              </div>
            )}
            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p>{uploadStatus || `${uploadProgress}% Uploaded`}</p>
              </div>
            )}
            {!uploading && uploadStatus && !uploadStatus.startsWith('Selected:') && (
              <p className="upload-status">{uploadStatus}</p>
            )}
          </div>
          
          {/* Requirement 11.3: Right-click on empty space shows context menu */}
          {filteredFolders.length > 0 && (
            <div 
              className="folders-list"
              onContextMenu={handleEmptySpaceContextMenu}
              data-testid="folders-list-container"
            >
              <h3>Folders</h3>
              <div className="folder-grid">
                {filteredFolders.map((folder) => (
                  <div 
                    key={folder} 
                    className={`folder ${checkIsSelected(folder) ? 'selected' : ''} ${isDropTarget(folder, dragState) ? 'drop-target' : ''} ${isItemBeingDragged(folder, dragState) ? 'dragging' : ''}`}
                    onClick={(e) => handleFolderClick(folder, e)}
                    onContextMenu={(e) => handleFolderContextMenu(e, folder)}
                    draggable="true"
                    onDragStart={(e) => onFolderDragStart(e, folder)}
                    onDragEnd={onDragEnd}
                    onDragOver={(e) => onFolderDragOver(e, folder)}
                    onDragLeave={onFolderDragLeave}
                    onDrop={(e) => onFolderDrop(e, folder)}
                    data-testid={`folder-item-${folder.replace(/\s+/g, '-')}`}
                  >
                    {renamingFolderName === folder ? (
                      <div className="folder-rename-container">
                        <InlineRenameInput
                          currentName={folder}
                          onSubmit={(newName) => handleFolderRename(folder, newName)}
                          onCancel={handleCancelFolderRename}
                          existingNames={folders.filter(f => f !== folder)}
                          placeholder="Enter folder name"
                        />
                        {isFolderRenaming && (
                          <div className="folder-rename-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-bar-fill" 
                                style={{ 
                                  width: folderRenameProgress.total > 0 
                                    ? `${Math.round((folderRenameProgress.completed / folderRenameProgress.total) * 100)}%` 
                                    : '0%' 
                                }}
                              ></div>
                            </div>
                            <span className="rename-progress-text">
                              {folderRenameProgress.total > 0 
                                ? `Renaming... ${folderRenameProgress.completed}/${folderRenameProgress.total}`
                                : 'Preparing...'}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : deletingFolderName === folder ? (
                      <div className="folder-delete-container">
                        <h3 className="folder-title">{folder}</h3>
                        <div className="folder-delete-progress">
                          <div className="progress-bar">
                            <div 
                              className="progress-bar-fill delete-progress-fill" 
                              style={{ 
                                width: folderDeleteProgress.total > 0 
                                  ? `${Math.round((folderDeleteProgress.completed / folderDeleteProgress.total) * 100)}%` 
                                  : '0%' 
                              }}
                            ></div>
                          </div>
                          <span className="delete-progress-text">
                            {folderDeleteProgress.total > 0 
                              ? `Deleting... ${folderDeleteProgress.completed}/${folderDeleteProgress.total}`
                              : 'Preparing to delete...'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Checkbox for folder selection - Requirement 8.4 */}
                        {/* Conditionally render checkbox only in Selection Mode - Requirements 1.4, 4.1 */}
                        {isSelectionMode && (
                          <input
                            type="checkbox"
                            className="item-checkbox folder-checkbox"
                            checked={checkIsSelected(folder)}
                            onChange={(e) => handleCheckboxClick(folder, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="folder-content">
                          <h3 className="folder-title">{folder}</h3>
                          <p>Click to open</p>
                        </div>
                        {/* Inline hover actions for folders - Requirement 12.3 */}
                        {/* 12.3: Actions: Rename, Delete */}
                        {/* Requirements 2.1, 2.3 (selection-mode-toggle): Only show in Normal Mode, no Open button */}
                        {!isSelectionMode && (
                        <div className="folder-item-actions" data-testid={`folder-inline-actions-${folder.replace(/\s+/g, '-')}`}>
                          <button 
                            className="inline-action-button rename-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingFolderName(folder);
                            }}
                            disabled={isFolderRenaming || isFolderDeleting || renamingFolderName !== null}
                            title="Rename"
                            aria-label="Rename folder"
                            data-testid={`folder-inline-action-rename-${folder.replace(/\s+/g, '-')}`}
                          >
                            <EditIcon size={14} />
                          </button>
                          <button 
                            className="inline-action-button delete-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFolderDelete(folder);
                            }}
                            disabled={isFolderRenaming || isFolderDeleting || renamingFolderName !== null}
                            title="Delete"
                            aria-label="Delete folder"
                            data-testid={`folder-inline-action-delete-${folder.replace(/\s+/g, '-')}`}
                          >
                            <DeleteIcon size={14} />
                          </button>
                        </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* File/Folder View - Conditional rendering based on viewMode */}
          {/* Requirements: 10.1, 10.2, 10.3, 10.5 */}
          {viewMode === 'list' ? (
            /* List View - Requirement 10.1: List view showing files in rows with details */
            /* Requirement 11.3: Right-click on empty space shows context menu */
            <div 
              className="file-list"
              onContextMenu={handleEmptySpaceContextMenu}
              data-testid="file-list-container"
            >
              <h3>Files</h3>
              {filteredFiles.length === 0 && !searchQuery ? (
                <p>No files in this folder yet.</p>
              ) : filteredFiles.length === 0 ? (
                null /* No results message is shown above when searching */
              ) : (
                <ul>
                  {filteredFiles.map((file) => (
                    <li 
                      key={file.key} 
                      className={`file-item ${checkIsSelected(file.key) ? 'selected' : ''} ${isItemBeingDragged(file.key, dragState) ? 'dragging' : ''}`}
                      onClick={(e) => handleItemSelect(file.key, e)}
                      onContextMenu={(e) => handleFileContextMenu(e, file)}
                      draggable="true"
                      onDragStart={(e) => onDragStart(e, file.key)}
                      onDragEnd={onDragEnd}
                      data-testid={`file-item-${file.name}`}
                    >
                      {/* Checkbox for file selection - Requirement 8.4 */}
                      {/* Conditionally render checkbox only in Selection Mode - Requirements 1.4, 4.1 */}
                      {isSelectionMode && (
                        <input
                          type="checkbox"
                          className="item-checkbox file-checkbox"
                          checked={checkIsSelected(file.key)}
                          onChange={(e) => handleCheckboxClick(file.key, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {renamingFileKey === file.key ? (
                        <div className="file-rename-container">
                          <InlineRenameInput
                            currentName={file.name}
                            onSubmit={(newName) => handleFileRename(file.key, newName)}
                            onCancel={handleCancelRename}
                            existingNames={files.filter(f => f.key !== file.key)}
                            placeholder="Enter file name"
                          />
                          {isRenaming && (
                            <span className="rename-loading">Renaming...</span>
                          )}
                        </div>
                      ) : (
                        <span className="file-name">
                          {file.name}
                          {file.status && file.status !== 'COMPLETE' && (
                            <span className="file-status-badge pending" title="Upload incomplete - file cannot be downloaded">⚠ Upload incomplete</span>
                          )}
                        </span>
                      )}
                      <span className="file-size">{file.size ? formatFileSize(file.size) : 'Unknown size'}</span>
                      <span className="file-date">{formatDate(file.updatedAt || file.createdAt)}</span>
                      {/* Inline hover actions - Requirements 12.1, 12.2 */}
                      {/* 12.1: Show action icons on hover */}
                      {/* 12.2: Actions: Download, Rename, Move, Delete */}
                      {/* Only show inline actions in Normal Mode (Requirement 2.2) */}
                      {!isSelectionMode && (
                        <div className="inline-actions" data-testid={`inline-actions-${file.name}`}>
                          <button 
                            className="inline-action-button download-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (file.status && file.status !== 'COMPLETE') {
                                setError('This file cannot be downloaded because the upload did not complete. You can delete it and re-upload.');
                                return;
                              }
                              handleDownload(file.id);
                            }}
                            disabled={isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving || (file.status && file.status !== 'COMPLETE')}
                            title={file.status && file.status !== 'COMPLETE' ? 'Upload incomplete - cannot download' : 'Download'}
                            aria-label="Download file"
                            data-testid={`inline-action-download-${file.name}`}
                          >
                            <DownloadIcon size={14} />
                          </button>
                          <button 
                            className="inline-action-button rename-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingFileKey(file.key);
                            }}
                            disabled={isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving || renamingFileKey !== null}
                            title="Rename"
                            aria-label="Rename file"
                            data-testid={`inline-action-rename-${file.name}`}
                          >
                            <EditIcon size={14} />
                          </button>
                          <button 
                            className="inline-action-button move-action"
                            onClick={async (e) => {
                              e.stopPropagation();
                              setFileToMove(file);
                              const folders = await fetchAllFolders();
                              setAllFolders(folders);
                              setShowMoveModal(true);
                            }}
                            disabled={isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving}
                            title="Move"
                            aria-label="Move file"
                            data-testid={`inline-action-move-${file.name}`}
                          >
                            <MoveIcon size={14} />
                          </button>
                          <button 
                            className="inline-action-button delete-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(file);
                            }}
                            disabled={isRenaming || isFolderRenaming || isFolderDeleting || isBulkDeleting || isBulkMoving}
                            title="Delete"
                            aria-label="Delete file"
                            data-testid={`inline-action-delete-${file.name}`}
                          >
                            <DeleteIcon size={14} />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            /* Grid View - Requirement 10.2: Grid view showing files as larger tiles */
            /* Requirement 12.4: Show action overlay on hover with same actions as list view */
            <GridView
              files={filteredFiles}
              folders={[]} /* Folders are rendered separately above */
              selectedItems={selectedItems}
              onItemSelect={handleItemSelect}
              onFolderOpen={navigateToFolder}
              onFileDownload={handleDownload}
              onFileMove={async (key) => {
                // Find the file object from the key
                const file = filteredFiles.find(f => f.key === key);
                if (file) {
                  setFileToMove(file);
                  const folders = await fetchAllFolders();
                  setAllFolders(folders);
                  setShowMoveModal(true);
                }
              }}
              onItemRename={(key) => {
                // Check if it's a file or folder and set appropriate state
                const isFile = filteredFiles.some(f => f.key === key);
                if (isFile) {
                  setRenamingFileKey(key);
                } else {
                  setRenamingFolderName(key);
                }
              }}
              onItemDelete={(key) => {
                // Check if it's a file or folder and call appropriate handler
                const file = filteredFiles.find(f => f.key === key);
                if (file) {
                  handleDelete(file);
                } else {
                  handleFolderDelete(key);
                }
              }}
              onCheckboxClick={handleCheckboxClick}
              disabled={isFolderRenaming || isFolderDeleting || isRenaming || isBulkDeleting || isBulkMoving || renamingFolderName !== null || renamingFileKey !== null}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onFolderDragOver={onFolderDragOver}
              onFolderDragLeave={onFolderDragLeave}
              onFolderDrop={onFolderDrop}
              isItemBeingDragged={(key) => isItemBeingDragged(key, dragState)}
              isDropTarget={(key) => isDropTarget(key, dragState)}
            />
          )}
            </>
          )}
        </div>
      )}
      
      {showMoveModal && (
        <div className="move-modal">
          <div className="move-modal-content">
            <h3>Move File</h3>
            <p>Select destination folder for: {fileToMove?.name}</p>
            
            <div className="folder-list">
              {allFolders.map((folder) => {
                // Calculate indentation level based on path
                const level = folder.path ? folder.path.split('/').length : 0;
                const indent = level * 16; // 16px per level
                
                return (
                  <div 
                    key={folder.id}
                    className="folder-item" 
                    onClick={() => !isMoving && handleMoveFile(folder)}
                    style={{ 
                      opacity: isMoving ? 0.5 : 1, 
                      cursor: isMoving ? 'not-allowed' : 'pointer',
                      paddingLeft: `${indent}px`
                    }}
                  >
                    {folder.path === '' ? 'Root' : folder.name}
                  </div>
                );
              })}
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => !isMoving && setShowMoveModal(false)} 
                disabled={isMoving}
              >
                {isMoving ? 'Moving...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Move Modal - Requirements 8.6, 8.7 */}
      {showBulkMoveModal && (
        <div className="move-modal bulk-move-modal">
          <div className="move-modal-content">
            <h3>Move {getSelectedCount()} Item(s)</h3>
            <p>Select destination folder:</p>
            
            {/* Progress indicator during bulk move */}
            {isBulkMoving && (
              <div className="bulk-move-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ 
                      width: bulkMoveProgress.total > 0 
                        ? `${Math.round((bulkMoveProgress.completed / bulkMoveProgress.total) * 100)}%` 
                        : '0%' 
                    }}
                  ></div>
                </div>
                <span className="move-progress-text">
                  {bulkMoveProgress.total > 0 
                    ? `Moving... ${bulkMoveProgress.completed}/${bulkMoveProgress.total}`
                    : 'Preparing...'}
                </span>
                {bulkMoveProgress.currentItem && (
                  <span className="current-item-text">
                    Current: {bulkMoveProgress.currentItem}
                  </span>
                )}
              </div>
            )}
            
            <div className="folder-list">
              {allFolders.map((folder) => {
                // Calculate indentation level based on path
                const level = folder.path ? folder.path.split('/').length : 0;
                const indent = level * 16; // 16px per level
                
                // Check if this folder is one of the selected items (can't move to itself)
                const selected = getSelected();
                const isSelectedFolder = selected.some(item => {
                  const fullPath = currentFolder ? `${currentFolder}/${item}` : item;
                  return folder.path === fullPath || folder.path.startsWith(`${fullPath}/`);
                });
                
                return (
                  <div 
                    key={folder.id}
                    className={`folder-item ${isSelectedFolder ? 'disabled' : ''}`}
                    onClick={() => !isBulkMoving && !isSelectedFolder && handleBulkMove(folder)}
                    style={{ 
                      opacity: isBulkMoving || isSelectedFolder ? 0.5 : 1, 
                      cursor: isBulkMoving || isSelectedFolder ? 'not-allowed' : 'pointer',
                      paddingLeft: `${indent}px`
                    }}
                    title={isSelectedFolder ? 'Cannot move folder into itself' : ''}
                  >
                    {folder.path === '' ? 'Root' : folder.name}
                    {isSelectedFolder && ' (selected)'}
                  </div>
                );
              })}
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => !isBulkMoving && setShowBulkMoveModal(false)} 
                disabled={isBulkMoving}
              >
                {isBulkMoving ? 'Moving...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Conflict Resolution Dialog - Requirements 5.4, 5.5 */}
      <ConflictResolutionDialog
        isOpen={conflictDialog.isOpen}
        itemName={conflictDialog.itemName}
        itemType={conflictDialog.itemType}
        destinationFolder={conflictDialog.destinationFolder}
        onReplace={handleConflictReplace}
        onRename={handleConflictRename}
        onSkip={handleConflictSkip}
        onClose={handleConflictSkip}
      />
      
      {/* Context Menu for file/folder right-click actions - Requirements 11.1, 11.2, 11.4 */}
      <ContextMenu
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
        items={
          shouldShowMultiSelectionMenu() 
            ? getMultiSelectionContextMenuItems() 
            : contextMenu.targetType === 'file' 
              ? getFileContextMenuItems() 
              : contextMenu.targetType === 'folder' 
                ? getFolderContextMenuItems() 
                : contextMenu.targetType === 'empty' 
                  ? getEmptySpaceContextMenuItems() 
                  : []
        }
        onClose={handleCloseContextMenu}
      />
      
    </div>
  );
};

export default FileExplorer;
